/* Generated automagically by FhirStarter - but please feel free to edit this file */
import React, { useContext } from 'react';

import moment from 'moment';

import CheckIcon from '@mui/icons-material/Check';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

import { searchScopes } from '@rs-core/context/consts/searchScopes';
import { SelectionContext } from '@rs-core/context/SelectionContext';
import { StudyStatusCellProvider } from '@rs-ui/components/Worklist/StudyStatusCell/StudyStatusCellProvider';
import { MemoizedPatientProfileCell as PatientProfileCell } from '@rs-ui/components/Worklist/WorklistGrid/cells/PatientProfileCell';
import OverflowTooltip from '@rs-ui/components/OverflowTooltip/OverflowTooltip';
import CheckboxCell from '@rs-ui/components/Worklist/WorklistGrid/cells/CheckboxCell';
import StatusIndicatorCell, {
	GetStatusIndicatorDisplay,
} from '@rs-ui/components/Worklist/WorklistGrid/cells/StatusIndicatorCell';

import { MemoizedInlineEditCell as InlineEditCell } from '@rs-ui/components/Worklist/WorklistGrid/cells/InlineEditCell';
import convertArrayToString from '@rs-core/fhir/resource/columnMapping/utils/convertArrayToString';
import getExtensionValueReference from '@rs-core/fhir/resource/columnMapping/utils/getExtensionValueAttachment';
import getWorklistParsedName from '@rs-core/fhir/resource/columnMapping/utils/getWorklistParsedName';
import calculatePatientAgeAtStudy from '@rs-core/fhir/resource/columnMapping/utils/calculatePatientAgeAtStudy';
import { sortCompareNumber } from '@rs-core/fhir/resource/columnMapping/utils/sortCompare';
import getUserFullName from '@rs-core/fhir/resource/columnMapping/utils/getUserFullName';
import getExtensionValueCodingArray from '@rs-core/fhir/resource/columnMapping/utils/getExtensionValueCodingArray';
import getExtensionValueCodingArrayConcat from '@rs-core/fhir/resource/columnMapping/utils/getExtensionValueCodingArrayConcat';
import getExtensionValueString from '@rs-core/fhir/resource/columnMapping/utils/getExtensionValueString';
import getExtensionValueCoding from '@rs-core/fhir/resource/columnMapping/utils/getExtensionValueCoding';

import { isValidDateMapping } from '@rs-ui/components/utils/dateUtils';
import fhirExtensionUrls from '@rs-core/fhir/extension/fhirExtensionUrls';
import CheckboxSelectAll from '@rs-ui/components/Worklist/WorklistGrid/cells/CheckboxSelectAll';
import { statusIndicatorFilterOptions } from '@worklist-2/core/src/fhir/resource/columnMapping/consts/columns';
import dateTimeUtil from '@rs-core/utils/dateTimeUtil';

/*
 * This function is to format the worklist datetime display
 * currently it will display in the format as "2024-08-14 4:38 PM"
 */
export const worklistDateTimeDisplay = value => {
	if (value) {
		const dateTimeCheck = moment(value);
		if (dateTimeCheck.isValid()) {
			let dateTime = dateTimeCheck.toDate();
			if (isValidDateMapping(dateTime)) {
				return `${dateTime.toLocaleDateString()} ${dateTime.toLocaleTimeString([], {
					hour: '2-digit',
					minute: '2-digit',
				})}`;
			}
		}
	}
};

export const handleOnUpdate = (props, response) => {
	props.table.options.meta?.updateData(
		props.row.index,
		'referringPhysician',
		response?.mavenReferringOrganizationNotUpdating ? response?.requester?.display : response,
		null,
		props.row?.original?.id
	);
	if (response?.mavenReferringOrganizationNotUpdating) {
		props.table.options.meta?.updateData(
			props.row.index,
			'referringFacility',
			response?.requester?.extension[0]?.valueReference?.display,
			null,
			props.row?.original?.id
		);
	}
};

/*
 * This function is to old format the worklist datetime display
 * for feature flag implementation.
 */
export const worklistOldDateTimeDisplay = (value, isLocale = true) => {
	if (value) {
		// Remove the timezone information from the date string
		const dateTimeCheck = moment(value);
		if (dateTimeCheck.isValid()) {
			let dateTime = dateTimeCheck.toDate();
			if (isValidDateMapping(dateTime)) {
				if (isLocale) {
					return `${dateTime.toLocaleDateString()} ${dateTime.toLocaleTimeString()}`;
				}
				return `${dateTime.toDateString()} ${dateTime.toTimeString()}`;
			}
		}
	}
};

// Use this function to get the content for the group row
const getGroupContent = (rowData, fieldName, valueType = 'string', iconComponent = null) => {
	const resource = rowData.original;
	if (resource?.rawData) {
		const groupData = resource.rawData[fieldName];
		let display = '';
		let numSubItems = 0;
		if (groupData) {
			display = groupData.value;
			if (display) {
				switch (valueType) {
					case 'date':
						if (display == '1900-01-01') {
							display = '';
						} else {
							display = dateTimeUtil.parseLocaleDate(display, 'date');
						}
						break;
					case 'time-minutes': {
						const hours = Math.floor(Number(display) / 60); // Calculate total hours
						const remainingMinutes = Number(display) % 60; // Calculate remaining minutes
						display = `${hours}:${remainingMinutes.toString().padStart(2, '0')}`;
						break;
					}
					case 'array': {
						try {
							const arrValue = JSON.parse(groupData.value);
							display = convertArrayToString(arrValue, 'display');
						} catch (e) {
							display = groupData.value;
						}
						break;
					}
					default:
						if (typeof display == 'string') {
							display = display?.replaceAll('|', ' | ');
						}
						break;
				}
			}

			if (groupData.subGroupCount || groupData.docCount) {
				numSubItems = groupData.subGroupCount || groupData.docCount;
			}
		}

		return (
			<>
				{iconComponent}
				{`${display}`?.toUpperCase()}
				{numSubItems && (
					<Box
						sx={{
							backgroundColor: 'rgba(0, 0, 0, 0.5)',
							borderRadius: '5px',
							height: '18px',
							padding: '1px 7px 1px 7px',
							display: 'inline-block',
							textAlign: 'center',
						}}
					>
						{`${numSubItems}`}
					</Box>
				)}
			</>
		);
	} else {
		return '';
	}
};

export const getPatientClassDisplay = patientClass => {
	return patientClass ? patientClass.replace(/\b\w/g, char => char.toUpperCase()) : patientClass;
};

const ImagingStudyWorklistMapping = {
	id: {
		label: '',
		options: {
			filter: false,
			sort: false,
			display: 'excluded',
			viewColumns: false,
			excludeFromSave: true,
			groupable: false,
		},
		queryParameter: 'id',
		valueSet: {},
		filterType: 'none',
		getDisplay: resource => resource.id,
	},
	accessionNumber: {
		label: 'Accession #',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		type: 'string',
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		searchParameter: 'accessionNumber',
		getDisplay: resource =>
			resource?.identifier?.find(elem => elem.type?.coding?.some(item => item.code === 'ACSN'))?.value,
		accessorFn: row => row?.identifier?.find(elem => elem.type?.coding?.some(item => item.code === 'ACSN'))?.value,
		getGroupDisplay: row => getGroupContent(row, 'accessionNumber', row.accessionNumber),
	},
	primaryPriorAuthorizationNumber: {
		label: 'Primary Prior Authorization #',
		options: { filter: true, sort: true, groupable: true },
		type: 'string',
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		searchParameter: 'primarypriorauthorizationnumber',
		accessorFn: row => row.primaryPriorAuthorizationNumber,
		getDisplay: resource => resource.primaryPriorAuthorizationNumber,
		getGroupDisplay: row =>
			getGroupContent(row, 'primaryPriorAuthorizationNumber', row.primaryPriorAuthorizationNumber),
	},
	assigningAuthority: {
		label: 'Assigning Authority',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		type: 'string',
		variableTypeToParseWhenSorting: 'text',
		filterType: 'none',
		searchParameter: 'assigningAuthority',
		getDisplay: resource =>
			resource.extension?.find(
				ext =>
					ext.url == fhirExtensionUrls.organization.issuer ||
					ext.url == fhirExtensionUrls.organization.defaultIssuer
			)?.valueReference.display,
		accessorFn: row =>
			row.extension?.find(
				ext =>
					ext.url == fhirExtensionUrls.organization.issuer ||
					ext.url == fhirExtensionUrls.organization.defaultIssuer
			)?.valueReference.display,
		getGroupDisplay: row => getGroupContent(row, 'assigningAuthority', row.assigningAuthority),
	},
	accountNumber: {
		label: 'Account #',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		type: 'string',
		filterType: 'text-search',
		searchParameter: 'accountNumber',
		getDisplay: resource => resource.accountNumber,
		getGroupDisplay: row => getGroupContent(row, 'accountNumber', row.accountNumber),
	},
	accountStatus: {
		label: 'Account Status',
		options: { filter: false, sort: true, groupable: true },
		type: 'string',
		filterType: 'multi-select',
		searchParameter: 'accountStatus',
		searchValueSet: 'accountStatus',
		displayValue: 'code',
		capitalizeOptions: true,
		getDisplay: resource => resource.accountStatus,
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'accountStatus' },
			},
		},
		getGroupDisplay: row => getGroupContent(row, 'accountStatus'),
	},
	gender: {
		label: 'Gender',
		options: { filter: true, sort: true, groupable: true },
		type: 'string',
		filterType: 'multi-select',
		searchParameter: 'gender',
		searchValueSet: 'sex',
		getDisplay: resource => resource.gender?.toUpperCase(),
		getGroupDisplay: row => getGroupContent(row, 'gender'),
	},
	availability: {
		label: 'Availability',
		options: { filter: false, sort: true, groupable: false },
		type: 'string',
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.availability,
		getGroupDisplay: row => getGroupContent(row, 'availability'),
	},
	authorizationPeriod: {
		label: 'Authorization Period',
		options: { filter: false, sort: true, groupable: false },
		type: 'string',
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.authorizationPeriod,
		getGroupDisplay: row => getGroupContent(row, 'authorizationPeriod'),
	},
	appointmentCancellationReason: {
		label: 'Appointment Cancellation Reason',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		type: 'string',
		filterType: 'none',
		searchParameter: 'cancellationReason',
		getDisplay: resource => resource.cancellationReason,
		getGroupDisplay: row => getGroupContent(row, 'appointmentCancellationReason'),
	},
	cdsId: {
		label: 'CDS ID',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		type: 'string',
		filterType: 'text-search',
		searchParameter: 'cdsId',
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.procedureRequest.cdsId),
		accessorFn: row => getExtensionValueString(row, fhirExtensionUrls.procedureRequest.cdsId),
		getGroupDisplay: row => getGroupContent(row, 'cdsId'),
	},
	sourceDeviceAETitle: {
		label: 'Source Device AE Title',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		type: 'string',
		filterType: 'none',
		searchParameter: 'sourcedeviceaetitle',
		getDisplay: resource => resource.sourceDeviceAETitle,
		getGroupDisplay: row => getGroupContent(row, 'sourceDeviceAETitle'),
	},
	smokingStatus: {
		label: 'Smoking Status',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: '',
		searchParameter: '',
		isTranslatable: false,
		type: 'string',
		getDisplay: resource => convertArrayToString(resource.smokingStatus, 'display'),
		accessorFn: row => convertArrayToString(row.smokingStatus, 'display'),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			searchExistingOptions: true,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'smokingStatus' },
			},
		},
		getGroupDisplay: row => getGroupContent(row, 'smokingStatus'),
	},
	statusIndicator: {
		label: 'Indicators',
		options: {
			filter: true,
			sort: false,
			useCustomExportFn: true,
			groupable: false,
		},
		type: 'string',
		filterType: 'checkbox-multi-select',
		searchParameter: 'statusIndicator',
		customFilterOptions: statusIndicatorFilterOptions,
		accessorFn: (row, index, column) => {
			let statusIndicators = GetStatusIndicatorDisplay(column, row);
			return statusIndicators.indicators.map(indicator => indicator.name)?.join('|');
		},
		cell: props => <StatusIndicatorCell column={props.column} row={props.row.original} />,
	},
	languages: {
		label: 'Languages',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		searchParameter: 'language',
		searchValueSet: 'language',
		type: 'string-array',
		getDisplay: resource => convertArrayToString(resource.languages),
		accessorFn: row => convertArrayToString(row.languages),
		getGroupDisplay: row => getGroupContent(row, 'languages'),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'language' },
			},
		},
	},
	modality: {
		label: 'Modality',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		variableTypeToParseWhenSorting: 'text',
		filterType: 'multi-select',
		searchParameter: 'modalitycode',
		searchValueSet: 'modality',
		type: 'string-array',
		getDisplay: resource => convertArrayToString(resource?.modality, 'code'),
		accessorFn: row => convertArrayToString(row?.modality, 'code'),
		getGroupDisplay: row => getGroupContent(row, 'modality'),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'modality' },
			},
		},
	},
	race: {
		label: 'Race',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		searchParameter: 'race',
		searchValueSet: 'race',
		type: 'string-array',
		getDisplay: resource => convertArrayToString(resource.race),
		accessorFn: row => convertArrayToString(row.race),
		getGroupDisplay: row => getGroupContent(row, 'race'),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'race' },
			},
		},
	},
	ethnicity: {
		label: 'Ethnicity',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'ethnicity' },
			},
		},
		searchParameter: 'ethnicity',
		searchValueSet: 'ethnicity',
		type: 'string-array',
		getDisplay: resource => {
			if (resource.ethnicity instanceof Array) {
				const filtered = resource.ethnicity.filter(elem => elem.code !== 'UNK');
				return convertArrayToString(filtered);
			}
			return resource.ethnicity?.display || resource.ethnicity;
		},
		accessorFn: row => {
			if (row.ethnicity instanceof Array) {
				const filtered = row.ethnicity.filter(elem => elem.code !== 'UNK');
				return convertArrayToString(filtered);
			}
			return row.ethnicity?.display || row.ethnicity;
		},
		getGroupDisplay: row => getGroupContent(row, 'ethnicity'),
	},
	patientEmail: {
		label: 'Patient Email',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		type: 'string',
		filterType: 'text-search',
		searchParameter: 'patientEmail',
		getDisplay: resource => resource.patientEmail,
		getGroupDisplay: row => getGroupContent(row, 'patientEmail'),
	},
	cellPhone: {
		label: 'Patient Cell Phone',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		type: 'string',
		filterType: 'text-search',
		searchParameter: 'cellPhone',
		getDisplay: resource => resource.cellPhone,
		getGroupDisplay: row => getGroupContent(row, 'cellPhone'),
	},
	customField1: {
		label: 'Custom Field 1',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		type: 'string',
		searchParameter: 'customField1',
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.common.customField1),
		getGroupDisplay: row => getGroupContent(row, 'customField1'),
	},
	customField2: {
		label: 'Custom Field 2',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		type: 'string',
		searchParameter: 'customField2',
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.common.customField2),
		getGroupDisplay: row => getGroupContent(row, 'customField2'),
	},
	dateTimeAddendum: {
		label: 'Date/Time Addendum',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'date-range',
		searchParameter: 'dateTimeAddendumutc',
		convertToUTC: true,
		getDisplay: resource => worklistDateTimeDisplay(resource.dateTimeAddendumUTC),
		accessorFn: row => worklistDateTimeDisplay(row.dateTimeAddendumUTC),
		getGroupDisplay: row => getGroupContent(row, 'dateTimeAddendum', 'date'),
		toolTip: true,
		type: 'time',
	},
	lastUpdated: {
		label: 'Date/Time Last Updated',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'date-range',
		searchParameter: 'lastupdated',
		convertToUTC: true,
		getDisplay: resource => worklistDateTimeDisplay(resource.meta?.lastUpdated),
		accessorFn: row => worklistDateTimeDisplay(row.meta?.lastUpdated),
		getGroupDisplay: row => getGroupContent(row, 'lastUpdated', 'date'),
		getOrignialValue: row => row.meta?.lastUpdated,
		toolTip: true,
		type: 'time',
	},
	dateTimeOrdered: {
		// this is the date/time that the study transitions to the ORDERED study status
		label: 'Date/Time Ordered',
		options: { filter: true, sort: true, groupable: true },
		variableTypeToParseWhenSorting: 'date',
		filterType: 'date-range',
		searchParameter: 'datetimeorderedutc',
		convertToUTC: true,
		getDisplay: resource => worklistDateTimeDisplay(resource.dateTimeOrderedUTC),
		getGroupDisplay: row => getGroupContent(row, 'dateTimeOrdered', 'date'),
		getOrignialValue: row => row.dateTimeOrderedUTC,
		toolTip: true,
		type: 'time',
	},
	dateTimeRead: {
		label: 'Date/Time Read',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'date-range',
		searchParameter: 'datetimereadutc',
		convertToUTC: true,
		getDisplay: resource => worklistDateTimeDisplay(resource.dateTimeReadUTC),
		getGroupDisplay: row => getGroupContent(row, 'dateTimeRead', 'date'),
		getOrignialValue: row => row.dateTimeReadUTC,
		toolTip: true,
		type: 'time',
	},
	dateTimeReceived: {
		label: 'Date/Time Received',
		options: { filter: true, sort: true, groupable: true },
		variableTypeToParseWhenSorting: 'date',
		filterType: 'date-range',
		searchParameter: 'datetimereceivedutc',
		convertToUTC: true,
		getDisplay: resource => worklistDateTimeDisplay(resource.dateTimeReceivedUTC),
		getGroupDisplay: row => getGroupContent(row, 'dateTimeReceived', 'date'),
		getOrignialValue: row => row.dateTimeReceivedUTC,
		toolTip: true,
		type: 'time',
	},
	dateTimeSigned: {
		label: 'Date/Time Signed',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'date-range',
		searchParameter: 'datetimesignedutc',
		convertToUTC: true,
		getDisplay: resource => worklistDateTimeDisplay(resource.dateTimeSignedUTC),
		getGroupDisplay: row => getGroupContent(row, 'dateTimeSigned', 'date'),
		getOrignialValue: row => row.dateTimeSignedUTC,
		toolTip: true,
		type: 'time',
	},
	dateTimeTranscribed: {
		label: 'Date/Time Transcribed',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'date-range',
		searchParameter: 'datetimetranscribedutc',
		convertToUTC: true,
		getDisplay: resource => worklistDateTimeDisplay(resource.dateTimeTranscribedUTC),
		getGroupDisplay: row => getGroupContent(row, 'dateTimeTranscribed', 'date'),
		getOrignialValue: row => row.dateTimeTranscribedUTC,
		toolTip: true,
		type: 'time',
	},
	dateTimeVerified: {
		label: 'Date/Time Verified',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'date-range',
		searchParameter: 'datetimeverifiedutc',
		convertToUTC: true,
		getDisplay: resource => worklistDateTimeDisplay(resource.dateTimeVerifiedUTC),
		getGroupDisplay: row => getGroupContent(row, 'dateTimeVerified', 'date'),
		getOrignialValue: row => row.dateTimeVerifiedUTC,
		toolTip: true,
		type: 'time',
	},
	dateTimeScheduled: {
		label: 'Status To Scheduled',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'date-range',
		searchParameter: 'datetimescheduled',
		groupSearchParameter: 'datetimescheduledutc',
		getDisplay: resource => worklistDateTimeDisplay(resource.dateTimeScheduled),
		getGroupDisplay: row => getGroupContent(row, 'dateTimeScheduled', 'date'),
		toolTip: true,
		type: 'time',
	},
	appointmentDateTime: {
		label: 'Scheduled Date/Time',
		options: { filter: true, sort: true, groupable: true },
		variableTypeToParseWhenSorting: 'date',
		filterType: 'date-range',
		searchParameter: 'appointmentDateTime',
		getDisplay: resource => worklistDateTimeDisplay(resource.appointmentDateTime),
		getGroupDisplay: row => getGroupContent(row, 'appointmentDateTime', 'date'),
		toolTip: true,
		type: 'time',
	},
	department: {
		label: 'Department',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: 'date' },
		filterType: 'text-search',
		searchParameter: 'department',
		type: 'string',
		getDisplay: resource => resource.department,
		getGroupDisplay: row => getGroupContent(row, 'department'),
	},
	eligibility: {
		label: 'Eligibility',
		options: { filter: false, sort: true, groupable: true },
		filterType: 'multi-select',
		searchParameter: 'eligibility',
		searchValueSet: 'ordereligibility',
		type: 'string',
		getDisplay: resource =>
			getExtensionValueCoding(resource, fhirExtensionUrls.procedureRequest.eligibility).display,
		getGroupDisplay: row => getGroupContent(row, 'eligibility'),
	},
	communicationStatusName: {
		label: 'Communication Status Name',
		options: { filter: false, sort: true, groupable: true },
		filterType: 'none',
		searchParameter: 'faxstatus',
		type: 'string',
		getDisplay: resource => resource.communicationStatusName,
		getGroupDisplay: row => getGroupContent(row, 'communicationStatusName'),
	},
	fillerOrderNumber: {
		label: 'Filler Order #',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'fillerOrderNumber',
		type: 'string',
		getDisplay: resource => resource.fillerOrderNumber,
		getGroupDisplay: row => getGroupContent(row, 'fillerOrderNumber'),
	},
	homePhone: {
		label: 'Home Phone',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'none',
		searchParameter: 'homePhone',
		type: 'string',
		getDisplay: resource => resource.homePhone,
		getGroupDisplay: row => getGroupContent(row, 'homePhone'),
	},
	patientAge: {
		label: 'Age at Study',
		options: { filter: false, sort: true, sortCompare: sortCompareNumber, groupable: false },
		filterType: 'none',
		type: 'number',
		searchParameter: '',
		getDisplay: resource => calculatePatientAgeAtStudy(resource),
		getGroupDisplay: row => getGroupContent(row, 'patientAge'),
	},
	numImages: {
		label: '# of Images',
		options: { filter: true, sort: true, sortCompare: sortCompareNumber, groupable: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		type: 'number',
		searchParameter: 'numImages',
		getDisplay: resource => resource.numImages,
		getGroupDisplay: row => getGroupContent(row, 'numImages'),
	},
	insuranceCopay: {
		label: 'Insurance Copay',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		type: 'string',
		searchParameter: 'insuranceCopay',
		getDisplay: resource => resource.insuranceCopay,
		getGroupDisplay: row => getGroupContent(row, 'insuranceCopay'),
	},
	insuranceExpiry: {
		label: 'Insurance Expiry',
		options: { filter: false, sort: true, groupable: false },
		filterType: 'none',
		searchParameter: '',
		type: 'string',
		getDisplay: resource => resource.insuranceExpiry,
	},
	insurancePayer: {
		label: 'Primary Insurance',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'payer',
		type: 'string',
		getDisplay: resource => resource.insurancePayer?.display,
		getGroupDisplay: row => getGroupContent(row, 'insurancePayer'),
	},
	insuranceStatus: {
		label: 'Insurance Status',
		options: { filter: false, sort: true, groupable: false },
		filterType: 'none',
		searchParameter: '',
		type: 'string',
		getDisplay: resource => resource.insuranceStatus,
	},
	managingOrganization: {
		label: 'Managing Organization',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		variableTypeToParseWhenSorting: 'text',
		filterType: 'suggest',
		searchParameter: 'internalManagingOrganizationID',
		searchByTextParameter: 'ManagingOrganization',
		groupSearchParameter: 'managingOrganization',
		suggestScope: {
			resource: 'organization',
			label: 'name',
			extraParam: {
				organizationtype: 'PROV',
			},
		},
		type: 'string',
		getDisplay: resource => resource.managingOrganization?.toUpperCase(),
		getGroupDisplay: row => getGroupContent(row, 'managingOrganization'),
	},
	patientLocation: {
		label: 'Patient Location',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'location',
		type: 'string',
		getDisplay: resource => resource.patientLocation,
		getGroupDisplay: row => getGroupContent(row, 'patientLocation'),
	},
	numFrames: {
		label: '# of Frames',
		options: { filter: true, sort: true, sortCompare: sortCompareNumber, groupable: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		type: 'number',
		searchParameter: 'numFrames',
		getDisplay: resource => resource.numFrames,
		getGroupDisplay: row => getGroupContent(row, 'numFrames'),
	},
	numberOfInstances: {
		label: '# of Instances',
		options: {
			filter: true,
			sort: true,
			sortCompare: sortCompareNumber,
			groupable: true,
		},
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		type: 'number',
		searchParameter: 'numinstances',
		getDisplay: resource => resource.numberOfInstances,
		getGroupDisplay: row => getGroupContent(row, 'numberOfInstances'),
	},
	numReports: {
		label: '# of Reports',
		options: { filter: true, sort: true, sortCompare: sortCompareNumber, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		type: 'number',
		searchParameter: 'numReports',
		getDisplay: resource => resource.numReports,
		getGroupDisplay: row => getGroupContent(row, 'numReports'),
	},
	orderCustomField1: {
		label: 'Order Custom Field 1',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		type: 'string',
		searchParameter: 'orderCustomField1',
		getDisplay: resource =>
			getExtensionValueString(resource, fhirExtensionUrls.imagingStudyWorklist.orderCustomField1),
		getGroupDisplay: row => getGroupContent(row, 'orderCustomField1'),
	},
	orderCustomField2: {
		label: 'Order Custom Field 2',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		type: 'string',
		searchParameter: 'orderCustomField2',
		getDisplay: resource =>
			getExtensionValueString(resource, fhirExtensionUrls.imagingStudyWorklist.orderCustomField2),
		getGroupDisplay: row => getGroupContent(row, 'orderCustomField2'),
	},
	orderCustomMemo: {
		label: 'Order Custom Memo',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'orderCustomMemo',
		type: 'string',
		getDisplay: resource =>
			getExtensionValueString(resource, fhirExtensionUrls.imagingStudyWorklist.orderCustomMemo1),
		getGroupDisplay: row => getGroupContent(row, 'orderCustomMemo'),
	},
	orderDateTime: {
		label: 'Order Placement Date/Time',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'date-range',
		searchParameter: 'orderdatetimeutc',
		convertToUTC: true,
		getDisplay: resource => worklistDateTimeDisplay(resource.orderDateTimeUTC),
		getGroupDisplay: row => getGroupContent(row, 'orderDateTime', 'date'),
		getOrignialValue: row => row.orderDateTimeUTC,
		toolTip: true,
		type: 'time',
	},
	orderStatus: {
		label: 'Order Status',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'multi-select',
		searchParameter: 'orderstatus',
		searchValueSet: 'requeststatus',
		displayValue: 'code',
		type: 'string',
		getDisplay: resource => resource.orderStatus,
		getGroupDisplay: row => getGroupContent(row, 'orderStatus'),
	},
	imagingOrganization: {
		label: 'Imaging Organization',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'suggest',
		searchParameter: 'imagingOrganizationID',
		searchByTextParameter: 'imagingOrganization',
		groupSearchParameter: 'imagingOrganization',
		suggestScope: {
			resource: 'organization',
			label: 'name',
			extraParam: {
				organizationtype: 'PROV',
				isimaging: true,
			},
		},
		type: 'string',
		getDisplay: resource => resource.imagingOrganization?.toUpperCase(),
		getGroupDisplay: row => getGroupContent(row, 'imagingOrganization'),
	},
	financialClass: {
		label: 'Financial Class',
		options: { filter: false, sort: true, groupable: true },
		filterType: 'multi-select',
		searchParameter: 'financialClass',
		searchValueSet: 'financialClass',
		type: 'string',
		getDisplay: resource => resource.financialClass?.display,
		getGroupDisplay: row => getGroupContent(row, 'financialClass'),
	},
	healthcareService: {
		label: 'Healthcare Service',
		options: { filter: false, sort: true, groupable: true },
		filterType: 'multi-select',
		searchParameter: 'healthcareService',
		searchValueSet: 'healthcareService',
		type: 'string',
		accessorFn: row => row.healthcareServiceName || row.HealthcareServiceName,
		getDisplay: resource => resource.healthcareServiceName || resource.HealthcareServiceName,
		getGroupDisplay: row => getGroupContent(row, 'healthcareService'),
	},
	orderOfAppropriateness: {
		label: 'Order Of Appropriateness',
		options: { filter: false, sort: true, groupable: true },
		filterType: 'multi-select',
		searchParameter: 'orderappropriateness',
		searchValueSet: 'appropriateness',
		type: 'string',
		getDisplay: resource =>
			getExtensionValueCoding(resource, fhirExtensionUrls.procedureRequest.cdsAppropriate).display,
		getGroupDisplay: row => getGroupContent(row, 'orderOfAppropriateness'),
	},
	pharmaceutical: {
		label: 'Pharmaceutical',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		searchParameter: 'pharmaceutical',
		searchValueSet: 'pharmaceutical',
		type: 'string-array',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.pharmaceutical),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'pharmaceutical' },
			},
		},
		getGroupDisplay: row => getGroupContent(row, 'pharmaceutical'),
	},
	patientBalance: {
		label: 'Patient Balance',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'none',
		searchParameter: 'patientBalance',
		type: 'string',
		getDisplay: resource => resource.patientBalance,
		getGroupDisplay: row => getGroupContent(row, 'patientBalance'),
	},
	patientID: {
		label: 'Patient ID',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		searchParameter: 'patientID',
		type: 'string',
		getDisplay: resource => resource.patientID,
		getGroupDisplay: row => getGroupContent(row, 'patientID'),
	},
	performingPhysician: {
		label: 'Performing Physician',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'performingPhysician',
		type: 'string',
		getDisplay: resource => getUserFullName(resource.performingPhysician, true),
		cell: props =>
			!props?.renderText ? (
				<InlineEditCell
					size="small"
					columnSize={props.column.columnDef.size}
					userName={props.getValue()}
					internalStudyId={props.row.original.id}
					searchParams={{
						ispractitionerroleactive: true,
						managingorganization: props.row.original?.internalManagingOrganizationID,
						isperformingphysician: true,
					}}
					onUpdate={performingPhysicianName => {
						props.table.options.meta?.updateData(
							props.row.index,
							'performingPhysician',
							performingPhysicianName,
							null,
							props.row?.original?.id
						);
					}}
					cellLabel="performingPhysician"
					label="performing physician"
					checkStudyReadOnly={true}
					organizationId={getOrganizationId(props.row.original)}
					studyStatus={getStudyStatus(props.row.original)}
				/>
			) : (
				<span>{getUserFullName(props.row.original?.performingPhysician, true)}</span>
			),
		getGroupDisplay: row =>
			getGroupContent(row, 'performingPhysician', 'string', <FolderSharedIcon fontSize="small" />),
	},
	performingPhysicianNPI: {
		label: 'Performing Physician NPI',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'performingPhysicianNPI',
		type: 'string',
		getDisplay: resource => resource.performingPhysicianNPI,
		getGroupDisplay: row => getGroupContent(row, 'performingPhysicianNPI'),
	},
	performingTechnologist: {
		label: 'Performing Technologist',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'performingTechnologist',
		type: 'string',
		getDisplay: resource => resource.performingTechnologist,
		cell: props =>
			!props?.renderText ? (
				<InlineEditCell
					size="small"
					columnSize={props.column.columnDef.size}
					userName={props.getValue()}
					internalStudyId={props.row.original.id}
					searchParams={{
						ispractitionerroleactive: true,
						managingorganization: props.row.original?.internalManagingOrganizationID,
						isperformingtechnologist: true,
					}}
					onUpdate={performingTechnologistName => {
						props.table.options.meta?.updateData(
							props.row.index,
							'performingTechnologist',
							performingTechnologistName,
							null,
							props.row?.original?.id
						);
					}}
					cellLabel="performingTechnologist"
					label="performing technologist"
					checkStudyReadOnly={true}
					organizationId={getOrganizationId(props.row.original)}
					studyStatus={getStudyStatus(props.row.original)}
				/>
			) : (
				<span>{props.getValue()}</span>
			),
		getGroupDisplay: row => getGroupContent(row, 'performingTechnologist', 'string', <FolderSharedIcon />),
	},
	placerOrderNumber: {
		label: 'Placer Order #',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'placerOrderNumber',
		type: 'string',
		getDisplay: resource => resource.placerOrderNumber,
		getGroupDisplay: row => getGroupContent(row, 'placerOrderNumber'),
	},
	procedureCode: {
		label: 'Procedure Code',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		mavenSAYTNewComponent: true,
		type: 'string-array',
		searchComponentConfig: {
			scope: searchScopes?.procedureCode,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.code} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.code}` }),
			compareOptionFunc: (option, value) => option.code === value?.tagDisplay || option.id === value?.id,
			searchParameters: {
				sort: 'code',
				search: 'code',
			},
		},
		searchParameter: 'procedureCode',
		displayValue: 'code',
		getDisplay: resource => {
			if (resource?.procedureCode) {
				const procedureCodeDisplay = [];
				const procedurecodeLength =
					resource.procedureCode && resource.procedureCode[0] && resource.procedureCode[0].coding
						? resource.procedureCode[0].coding.length
						: 0;
				let item;

				for (let i = 0; i < procedurecodeLength; i++) {
					item = resource.procedureCode[0]?.coding[i];
					if (item && item.code) {
						procedureCodeDisplay.push(item.code);
					} else if (item && item.display) {
						procedureCodeDisplay.push(item.display);
					}
				}
				return convertArrayToString(procedureCodeDisplay);
			}

			return resource.procedureCode;
		},
		getGroupDisplay: row => getGroupContent(row, 'procedureCode'),
	},
	readingFacility: {
		label: 'Reading Organization',
		isTranslatable: false,
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'suggest',
		searchParameter: 'readingFacility',
		suggestScope: {
			resource: 'organization',
			label: 'name',
			extraParam: {
				organizationtype: 'PROV',
				isreading: true,
			},
		},
		mavenSAYTNewComponent: true,
		type: 'string',
		searchComponentConfig: {
			scope: searchScopes?.organization,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.name} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.name}` }),
			compareOptionFunc: (option, value) => {
				return option.name === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'name',
				search: 'name',
				extraParam: {
					isreading: true,
					organizationType: 'PROV',
				},
			},
		},
		getDisplay: resource => resource.readingFacility,
		getGroupDisplay: row => getGroupContent(row, 'readingFacility'),
	},
	readingPhysician: {
		label: 'Reading Physician',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'text-search',
		searchParameter: 'readingPhysician',
		isTranslatable: false,
		variableTypeToParseWhenSorting: 'text',
		type: 'string',
		getDisplay: resource => resource.readingPhysician,
		cell: props =>
			!props?.renderText ? (
				<InlineEditCell
					size="small"
					columnSize={props.column.columnDef.size}
					userName={props.getValue()}
					internalStudyId={props.row.original.id}
					searchParams={{
						ispractitionerroleactive: true,
						managingorganization: props.row.original?.internalManagingOrganizationID,
						isreadingphysician: true,
					}}
					onUpdate={readingPhysicianName => {
						props.table.options.meta?.updateData(
							props.row.index,
							'readingPhysician',
							readingPhysicianName,
							null,
							props.row?.original?.id
						);
					}}
					cellLabel="readingPhysician"
					label="reading physician"
					checkStudyReadOnly={true}
					organizationId={getOrganizationId(props.row.original)}
					studyStatus={getStudyStatus(props.row.original)}
				/>
			) : (
				<span>{props.row.original?.readingPhysician}</span>
			),
		getGroupDisplay: row =>
			getGroupContent(row, 'readingPhysician', 'string', <FolderSharedIcon fontSize="small" />),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.practitioner,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.name[0]?.text} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.name[0]?.text}` }),
			compareOptionFunc: (option, value) => {
				return option.name[0]?.text === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'name',
				search: 'name',
				extraParam: {
					isreadingphysician: true,
				},
			},
		},
	},
	readingPhysicianNPI: {
		label: 'Reading Physician NPI',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'readingPhysicianNPI',
		type: 'string',
		getDisplay: resource => resource.readingPhysicianNPI,
		getGroupDisplay: row => getGroupContent(row, 'readingPhysicianNPI'),
	},
	patientAddress: {
		label: 'Patient Address',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'address',
		type: 'string',
		getDisplay: resource => resource.patientAddress,
		getGroupDisplay: row => getGroupContent(row, 'patientAddress'),
	},
	referringFacility: {
		label: 'Referring Organization',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'suggest',
		searchParameter: 'referringFacilityID',
		groupSearchParameter: 'referringFacility',
		isTranslatable: false,
		variableTypeToParseWhenSorting: 'text',
		suggestScope: {
			resource: 'organization',
			label: 'name',
			extraParam: {
				organizationtype: 'PROV',
				isreferring: true,
			},
		},
		type: 'string',
		getDisplay: resource => resource.referringFacility,
		getGroupDisplay: row => getGroupContent(row, 'referringFacility', 'array'),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.organization,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.name} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.name}` }),
			compareOptionFunc: (option, value) => {
				return option.name === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'name',
				search: 'name',
				extraParam: {
					isreferring: true,
				},
			},
		},
	},
	referringPhysician: {
		label: 'Referring Physician',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'referringPhysician',
		isTranslatable: false,
		variableTypeToParseWhenSorting: 'text',
		type: 'string',
		getDisplay: resource => ({
			accessionNumber: resource.identifier?.find(elem => elem.type?.coding?.some(item => item.code === 'ACSN'))
				?.value,
			referringPhysician: getUserFullName(resource.referringPhysician),
		}),
		cell: props =>
			!props?.renderText ? (
				<InlineEditCell
					size="small"
					columnSize={props.column.columnDef.size}
					userName={props.getValue()}
					searchParams={{
						ispractitionerroleactive: true,
						managingorganization: props.row.original?.internalManagingOrganizationID,
						isreferringphysician: true,
						_elements: 'name',
						active: true,
					}}
					onUpdate={response => handleOnUpdate(props, response)}
					cellLabel="referringPhysician"
					internalOrderId={props.row.original?.basedOn?.[0]?.id}
					referringPhysicianId={props.row.original?.referringPhysicianId}
					label="referring physician"
				/>
			) : (
				<span>{props.getValue()}</span>
			),
		accessorFn: row => getUserFullName(row.referringPhysician),
		getGroupDisplay: row =>
			getGroupContent(row, 'referringPhysician', 'string', <FolderSharedIcon fontSize="small" />),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.practitioner,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.name[0]?.text} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.name[0]?.text}` }),
			compareOptionFunc: (option, value) => {
				return option.name[0]?.text === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'name',
				search: 'name',
				extraParam: {
					isreferringphysician: true,
					ispractitionerroleactive: true,
				},
			},
		},
	},
	referringPhysicianNPI: {
		label: 'Referring Physician NPI',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'referringPhysicianNPI',
		type: 'string',
		getDisplay: resource => resource.referringPhysicianNPI,
		getGroupDisplay: row => getGroupContent(row, 'referringPhysicianNPI'),
	},
	requestedAppointmentDateTime: {
		label: 'Requested Appointment Date/Time',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'date-range',
		searchParameter: 'requestedappointmentdatetimeutc',
		convertToUTC: true,
		type: 'time',
		getDisplay: resource => worklistDateTimeDisplay(resource.requestedAppointmentDateTimeUTC),
		getGroupDisplay: row => getGroupContent(row, 'requestedAppointmentDateTime', 'date'),
		getOrignialValue: row => row.requestedAppointmentDateTimeUTC,
		toolTip: true,
	},
	birthDate: {
		label: 'Date of Birth',
		options: { filter: false, sort: true, groupable: true },
		filterType: 'date-time',
		searchParameter: 'birthdate:exact',
		groupSearchParameter: 'birthDate',
		type: 'time',
		getDisplay: resource => {
			const value = resource.birthDate;
			if (value != null) {
				const dateTime = new Date(value);
				if (isValidDateMapping(dateTime)) {
					return moment(resource.birthDate).format('MM/DD/YYYY');
				}
			}
		},
		getGroupDisplay: row => getGroupContent(row, 'birthDate', 'date'),
		getOrignialValue: row => row.birthDate,
		toolTip: true,
	},
	birthSex: {
		label: 'Birth Sex',
		options: { filter: true, sort: true, groupable: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'multi-select',
		searchParameter: 'sex',
		searchValueSet: 'sex',
		type: 'string',
		getDisplay: resource =>
			resource.extension?.find(elem => elem.url === fhirExtensionUrls.common.birthSex)?.valueCode,
		getGroupDisplay: row => getGroupContent(row, 'birthSex'),
	},
	ssn: {
		label: 'SSN',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'ssn',
		type: 'string',
		getDisplay: resource => resource.ssn,
		getGroupDisplay: row => getGroupContent(row, 'ssn'),
	},
	state: {
		label: 'Patient State/Province',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'state',
		type: 'string',
		getDisplay: resource => resource.state,
		getGroupDisplay: row => getGroupContent(row, 'state'),
	},
	studyCustomMemo: {
		label: 'Study Custom Memo',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'studyCustomMemo',
		type: 'string',
		getDisplay: resource => resource.studyCustomMemo,
		getGroupDisplay: row => getGroupContent(row, 'studyCustomMemo'),
	},
	studyComment: {
		label: 'Study Comment',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'studyComment',
		type: 'string',
		getDisplay: resource =>
			resource.extension?.find(elem => elem.url === fhirExtensionUrls.imagingStudy.studyComments)?.valueString,
		getGroupDisplay: row => getGroupContent(row, 'studyComment'),
	},
	studyID: {
		label: 'Study ID',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		searchParameter: 'studyID',
		type: 'string',
		getDisplay: resource => resource.studyID,
		getGroupDisplay: row => getGroupContent(row, 'studyID'),
	},
	studyUID: {
		label: 'Study UID',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'text-search',
		searchParameter: 'studyUID',
		type: 'string',
		getDisplay: resource =>
			resource.identifier
				? resource.identifier?.find(elem => elem.system === 'urn:dicom:uid')?.value.replace(/^(urn:oid:)/, '')
				: '',
		getGroupDisplay: row => getGroupContent(row, 'studyUID'),
	},
	seriesUID: {
		label: 'Series UID',
		options: {
			filter: false,
			sort: false,
			display: 'excluded',
			viewColumns: false,
			groupable: false,
		},
		filterType: 'none',
		searchParameter: '',
		type: 'string',
		getDisplay: resource =>
			getExtensionValueString(resource, fhirExtensionUrls.imagingStudyWorklist.seriesInstanceUID),
	},
	sopInstanceUID: {
		label: 'SOP Instance UID',
		options: {
			filter: false,
			sort: false,
			display: 'excluded',
			viewColumns: false,
			groupable: false,
		},
		filterType: 'none',
		searchParameter: '',
		type: 'string',
		getDisplay: resource =>
			getExtensionValueString(resource, fhirExtensionUrls.imagingStudyWorklist.sopInstanceUID),
	},
	timeAtStatus: {
		label: 'Time At Status',
		options: { filter: false, sort: true, groupable: true, isTimeNumber: true },
		filterType: 'none',
		searchParameter: 'timeAtStatus',
		type: 'time',
		getDisplay: resource => resource.timeAtStatus,
		getGroupDisplay: row => getGroupContent(row, 'timeAtStatus', 'time-minutes'),
	},
	timeZone: {
		label: 'Time Zone',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		searchParameter: 'timeZone',
		searchValueSet: 'timeZone',
		type: 'string',
		getDisplay: resource => resource.timeZone,
		getGroupDisplay: row => getGroupContent(row, 'timeZone'),
	},
	transcriptionist: {
		label: 'Transcriptionist',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'transcriptionist',
		type: 'string',
		getDisplay: resource => resource.transcriptionist,
		cell: props =>
			!props?.renderText ? (
				<InlineEditCell
					size="small"
					columnSize={props.column.columnDef.size}
					userName={props.getValue()}
					internalStudyId={props.row.original.id}
					searchParams={{
						ispractitionerroleactive: true,
						managingorganization: props.row.original?.internalManagingOrganizationID,
						istranscriptionist: true,
					}}
					onUpdate={transcriptionistName => {
						props.table.options.meta?.updateData(
							props.row.index,
							'transcriptionist',
							transcriptionistName,
							null,
							props.row?.original?.id
						);
					}}
					cellLabel="transcriptionist"
					label="transcriptionist"
					checkStudyReadOnly={true}
					organizationId={getOrganizationId(props.row.original)}
					studyStatus={getStudyStatus(props.row.original)}
				/>
			) : (
				<span>{props.getValue()}</span>
			),
		getGroupDisplay: row =>
			getGroupContent(row, 'transcriptionist', 'string', <FolderSharedIcon fontSize="small" />),
	},
	transcriptionistFacility: {
		label: 'Transcriptionist Organization',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'suggest',
		searchParameter: 'transcriptionistFacility',
		suggestScope: {
			resource: 'organization',
			label: 'name',
			extraParam: {
				organizationtype: 'PROV',
				istranscription: true,
			},
		},
		type: 'string',
		getDisplay: resource => resource.transcriptionistFacility,
		getGroupDisplay: row => getGroupContent(row, 'transcriptionistFacility', 'array'),
	},
	specialArrangement: {
		label: 'Special Arrangement',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		searchParameter: 'specialArrangement',
		searchValueSet: 'specialArrangement',
		type: 'string',
		getDisplay: resource => resource.specialArrangement,
		getGroupDisplay: row => getGroupContent(row, 'specialArrangement'),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'specialArrangement' },
			},
		},
	},
	visitNumber: {
		label: 'Visit #',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		type: 'string',
		searchParameter: 'visitnumber',
		getDisplay: resource => resource.visitNumber,
		getGroupDisplay: row => getGroupContent(row, 'visitNumber'),
	},
	orderNotes: {
		label: 'Order Notes',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'orderNotes',
		type: 'string',
		getDisplay: resource => resource.orderNotes,
		getGroupDisplay: row => getGroupContent(row, 'orderNotes'),
	},
	clinicalNotes: {
		label: 'Clinical Notes',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'clinical',
		type: 'string',
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.imagingStudy.notes.clinical),
		getGroupDisplay: row => getGroupContent(row, 'clinicalNotes'),
	},
	history: {
		label: 'History',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'history',
		type: 'string',
		getDisplay: resource => resource.history,
		getGroupDisplay: row => getGroupContent(row, 'history'),
	},
	laterality: {
		label: 'Laterality',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		searchParameter: 'laterality',
		searchValueSet: 'laterality',
		type: 'string-array',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.laterality),
		getGroupDisplay: row => getGroupContent(row, 'laterality'),
	},
	bodyPart: {
		label: 'Body Part',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		mavenSAYTNewComponent: true,
		type: 'string-array',
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'bodyPart' },
			},
		},
		searchParameter: 'bodyPart',
		searchValueSet: 'bodyPart',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.bodyPart),
		getGroupDisplay: row => getGroupContent(row, 'bodyPart'),
	},
	modalityModifierArray: {
		label: 'Modality Modifier Array',
		options: { filter: false, sort: true, groupable: false },
		filterType: 'none',
		searchParameter: 'notes',
		type: 'string',
		getDisplay: resource => resource.modalityModifierArray,
	},
	anatomicFocus: {
		label: 'Anatomic Focus',
		options: { filter: false, sort: true, groupable: true },
		filterType: 'multi-select',
		mavenSAYTNewComponent: true,
		type: 'string-array',
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'anatomicFocus' },
			},
		},
		searchParameter: 'anatomicFocus',
		searchValueSet: 'anatomicFocus',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.anatomicFocus),
		getGroupDisplay: row => getGroupContent(row, 'anatomicFocus'),
	},
	priority: {
		label: 'Priority',
		options: { filter: false, sort: true, groupable: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'multi-select',
		searchParameter: 'priority',
		searchValueSet: 'priority',
		type: 'string',
		getDisplay: resource => {
			const routineValue =
				getExtensionValueReference(resource, fhirExtensionUrls.studyPriority.customPriority)?.display ||
				getExtensionValueCoding(resource, fhirExtensionUrls.common.priority)?.display;
			return routineValue?.toUpperCase();
		},
		cell: props => (
			<InlineEditCell
				size="small"
				cellValue={props}
				searchValueSet="priority"
				onUpdate={(priority, proactEnableCustomStudyPriority, updatedPriority) => {
					const extensionUrl = fhirExtensionUrls.common.priority;
					if (proactEnableCustomStudyPriority) {
						const customPriorityIdx = props.row.original.extension.findIndex(
							ext => ext.url == fhirExtensionUrls.studyPriority.customPriority
						);
						if (customPriorityIdx === -1) {
							const customExtension = {
								url: fhirExtensionUrls.studyPriority.customPriority,
								valueReference: updatedPriority,
							};
							props.row.original.extension.push(customExtension);
						} else {
							props.table.options.meta?.updateData(
								props.row.index,
								`extension[${customPriorityIdx}]valueReference`,
								updatedPriority,
								null,
								props.row?.original?.id
							);
						}
					} else {
						props.table.options.meta?.updateData(
							props.row.index,
							'priority',
							priority,
							extensionUrl,
							props.row?.original?.id
						);
					}
				}}
				cellLabel="priority"
				internalOrderId={props.row.original?.basedOn?.[0]?.id}
				imagingOrganizationId={props?.row?.original?.internalManagingOrganizationID}
			/>
		),
		getGroupDisplay: row => getGroupContent(row, 'priority'),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'priority' },
			},
		},
	},
	reasonForExam: {
		label: 'Reason For Exam',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		searchParameter: 'reasonForExam',
		searchValueSet: 'reasonForExam',
		type: 'string-array',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.reasonForExam),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: {
					'name:exact': 'reasonForExam',
				},
			},
		},
		getGroupDisplay: row => getGroupContent(row, 'reasonForExam'),
	},
	view: {
		label: 'View',
		options: { filter: false, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		searchParameter: 'view',
		searchValueSet: 'view',
		type: 'string-array',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.view),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'view' },
			},
		},
		getGroupDisplay: row => getGroupContent(row, 'view'),
	},
	techniqueObject: {
		label: 'Technique Object',
		options: { filter: false, sort: true, groupable: true },
		filterType: 'none',
		searchParameter: '',
		type: 'string',
		getDisplay: resource => resource.techniqueObject,
		getGroupDisplay: row => getGroupContent(row, 'techniqueObject'),
	},
	studyDateTime: {
		label: 'Study Date/Time',
		options: { filter: true, sort: true, groupable: true },
		variableTypeToParseWhenSorting: 'date',
		filterType: 'date-range',
		searchParameter: 'studydatetime',
		type: 'time',
		getDisplay: resource => worklistDateTimeDisplay(resource.started),
		accessorFn: row => worklistDateTimeDisplay(row.started),
		getGroupDisplay: row => getGroupContent(row, 'studyDateTime', 'date'),
		getOrignialValue: row => row.started,
		toolTip: true,
	},
	started: {
		label: 'Started',
		options: { filter: false, sort: true, groupable: true },
		filterType: 'none',
		searchParameter: 'studydatetime',
		type: 'time',
		getDisplay: resource => {
			if (resource.resourceType === 'ImagingStudyWorklistGroup') {
				const groupData = resource.rawData['started'];
				let display = '';
				if (groupData) {
					display = groupData.value;

					if (groupData.subGroupCount || groupData.docCount) {
						display += ` (${groupData.subGroupCount || groupData.docCount})`;
					}
				}
				return `${display}`.toUpperCase();
			}
			const value = resource.started;
			if (value != null) {
				const dateTime = new Date(value);
				if (!isNaN(dateTime.getTime()) && dateTime.getYear() >= 0) {
					return resource.started;
				}
			}
		},
		getGroupDisplay: row => getGroupContent(row, 'started', 'date'),
		getOrignialValue: row => row.started,
	},
	patientName: {
		label: 'Patient Name',
		options: {
			filter: true,
			sort: true,
			setCellProps: () => ({ style: { width: '0%' } }),
			groupable: true,
			isGroupExactMatch: true,
		},
		variableTypeToParseWhenSorting: 'text',
		filterType: 'text-search',
		queryType: 'multi-match',
		searchParameter: 'patientName',
		type: 'string',
		getDisplay: resource => `${getUserFullName(resource.subject?.display)}`,
		cell: props => <PatientProfileCell data={props.row.original} />,
		getGroupDisplay: row =>
			getGroupContent(
				row,
				'patientName',
				false,
				<AccountCircleIcon
					data-testid="account-icon"
					fontSize="small"
					sx={{
						color: '#FFFFFF',
						borderRadius: 4,
					}}
				/>
			),
	},
	patientgiven: {
		label: 'Patient First Name',
		options: {
			filter: true,
			sort: true,
			setCellProps: () => ({ style: { width: '0%' } }),
			groupable: true,
			isGroupExactMatch: true,
		},
		variableTypeToParseWhenSorting: 'text',
		filterType: 'text-search',
		searchParameter: 'patientgiven',
		type: 'string',
		getDisplay: resource => `${getWorklistParsedName(resource?.subject?.display)?.firstName}`,
		cell: props => getWorklistParsedName(props.row.original.subject?.display)?.firstName,
		getGroupDisplay: row => getGroupContent(row, 'patientgiven'),
	},
	patientfamily: {
		label: 'Patient Last Name',
		options: {
			filter: true,
			sort: true,
			setCellProps: () => ({ style: { width: '0%' } }),
			groupable: true,
			isGroupExactMatch: true,
		},
		variableTypeToParseWhenSorting: 'text',
		filterType: 'text-search',
		searchParameter: 'patientfamily',
		type: 'string',
		getDisplay: resource => `${getWorklistParsedName(resource?.subject?.display)?.lastName}`,
		cell: props => getWorklistParsedName(props.row.original.subject?.display)?.lastName,
		getGroupDisplay: row => getGroupContent(row, 'patientfamily'),
	},
	studyStatus: {
		label: 'Study Status',
		options: {
			filter: true,
			sort: true,
			group: true,
			customBodyRender: (value, tableMeta, updateValue) =>
				value?.props.text ? (
					<StudyStatusCellProvider
						studyId={value.props.text.studyId}
						status={value.props.text.status}
						onUpdate={(studyId, studyStatus) => {
							updateValue(
								<OverflowTooltip
									enableRecalc={false}
									text={{
										studyId,
										status: studyStatus,
									}}
								/>
							);
						}}
					/>
				) : null,
			setCellProps: () => ({ style: { width: '0%' } }),
			groupable: true,
		},
		filterType: 'multi-select',
		searchParameter: 'status',
		searchValueSet: 'status',
		type: 'string',
		getDisplay: resource => ({
			studyId: resource.id,
			status: getExtensionValueCoding(resource, fhirExtensionUrls.common.studyStatus).display,
		}),
		getOrignialValue: row => getExtensionValueCoding(row, fhirExtensionUrls.common.studyStatus).display,
		accessorFn: row => getExtensionValueCoding(row, fhirExtensionUrls.common.studyStatus)?.display,
		cell: props => (
			<StudyStatusCellProvider
				studyId={props.row.original.id}
				status={props.getValue()}
				orgId={props.row.original?.imagingOrganizationId || props.row.original.internalManagingOrganizationID}
				onUpdate={value => {
					const studyStatusIdx = props.row.original.extension?.findIndex(
						ext => ext.url == fhirExtensionUrls.common.studyStatus
					);
					props.table.options.meta?.updateData(
						props.row.index,
						`extension[${studyStatusIdx}]valueCoding.display`,
						value,
						null,
						props.row?.original?.id
					);
				}}
				rejectionReasonArr={getExtensionValueCodingArray(
					props.row.original,
					fhirExtensionUrls.imagingStudy.rejectionReason
				)}
				managingOrganization={props.row.original?.managingOrganization}
				managingOrganizationId={props.row.original?.internalManagingOrganizationID}
			/>
		),
		getGroupDisplay: row => getGroupContent(row, 'studyStatus'),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'status' },
			},
		},
	},
	studyDescription: {
		label: 'Study Description',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'description',
		type: 'string',
		getDisplay: resource => resource.description,
		getGroupDisplay: row => getGroupContent(row, 'studyDescription'),
	},
	requestedProcedureID: {
		label: 'Requested Procedure ID',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'text-search',
		searchParameter: 'requestedProcedureID',
		type: 'string',
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.imagingStudy.requestedProcedureID),
		getGroupDisplay: row => getGroupContent(row, 'requestedProcedureID'),
	},
	workflowStep: {
		label: 'Workflow Step',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'multi-select',
		searchParameter: 'workflowStep',
		searchValueSet: 'workflowStatus',
		valueSetExtraParam: { operator: '=' },
		type: 'string',
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.organization.workflowStep),
		getGroupDisplay: row => getGroupContent(row, 'workflowStep'),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'workflowStatus' },
			},
		},
	},
	flagCol: {
		label: ' ',
		customHeader: props => <CheckboxSelectAll rows={props.rows} />,
		getDisplay: resource => {
			const dicomUid = resource.identifier
				?.find(id => id.system === 'urn:dicom:uid')
				?.value?.split('urn:oid:')?.[1];

			return JSON.stringify({
				id: resource.id,
				isCritical: !!resource.extension?.find(elem => elem.url === fhirExtensionUrls.imagingStudy.isCritical)
					?.valueBoolean,
				dicomUid,
				internalManagingOrganizationId: resource.internalManagingOrganizationID,
				referringPhysicianId: resource.referringPhysicianId,
				accessionNumber: resource.identifier?.find(elem =>
					elem.type?.coding?.some(item => item.code === 'ACSN')
				)?.value,
			});
		},
		options: {
			customBodyRender: value => <CheckBoxOrCritical text={value.props.text} />,
			setCellProps: () => ({ style: { width: '0%' } }),
			viewColumns: false,
			draggable: false,
			excludeFromSave: true,
			groupable: false,
		},
		cell: props => {
			if (props.row.original.resourceType === 'ImagingStudyWorklistGroup') {
				return '';
			}

			return (
				<CheckboxCell
					data={{
						id: props.row.original.id,
						isCritical: !!props.row.original.extension?.find(
							elem => elem.url === fhirExtensionUrls.imagingStudy.isCritical
						)?.valueBoolean,
						dicomUid: props.row.original.identifier
							?.find(id => id.system === 'urn:dicom:uid')
							?.value?.split('urn:oid:')?.[1],
						internalManagingOrganizationId: props.row.original.internalManagingOrganizationID,
						referringPhysician: props.row.original.referringPhysician,
						referringPhysicianId: props.row.original.referringPhysicianId,
						accessionNumber: props.row.original.identifier?.find(elem =>
							elem.type?.coding?.some(item => item.code === 'ACSN')
						)?.value,
						basedOn: props.row.original.basedOn,
						patientID: props.row.original.patientID,
						patientEmail: props.row.original.patientEmail,
						subject: props.row.original.subject,
						rawData: props.row.original.rawData,
					}}
				/>
			);
		},
	},
	flagColTF: {
		label: ' ',
		customHeader: props => <CheckboxSelectAll rows={props.rows} />,
		getDisplay: resource => {
			const dicomUid = resource.identifier
				?.find(id => id.system === 'urn:dicom:uid')
				?.value?.split('urn:oid:')?.[1];

			return JSON.stringify({
				id: resource.id,
				isCritical: !!resource.extension?.find(elem => elem.url === fhirExtensionUrls.imagingStudy.isCritical)
					?.valueBoolean,
				dicomUid,
				internalManagingOrganizationId: resource.internalManagingOrganizationID,
				referringPhysicianId: resource.referringPhysicianId,
				accessionNumber: resource.identifier?.find(elem =>
					elem.type?.coding?.some(item => item.code === 'ACSN')
				)?.value,
			});
		},
		options: {
			customBodyRender: value => <CheckBoxOrCritical text={value.props.text} />,
			setCellProps: () => ({ style: { width: '0%' } }),
			viewColumns: false,
			draggable: false,
			excludeFromSave: true,
			groupable: false,
		},
		cell: props => (
			<CheckboxCell
				data={{
					id: props.row.original.id,
					isCritical: !!props.row.original.extension?.find(
						elem => elem.url === fhirExtensionUrls.imagingStudy.isCritical
					)?.valueBoolean,
					dicomUid: props.row.original.identifier
						?.find(id => id.system === 'urn:dicom:uid')
						?.value?.split('urn:oid:')?.[1],
					internalManagingOrganizationId: props.row.original.internalManagingOrganizationID,
					referringPhysician: props.row.original.referringPhysician,
					referringPhysicianId: props.row.original.referringPhysicianId,
					accessionNumber: props.row.original.identifier?.find(elem =>
						elem.type?.coding?.some(item => item.code === 'ACSN')
					)?.value,
					basedOn: props.row.original.basedOn,
					patientID: props.row.original.patientID,
					patientEmail: props.row.original.patientEmail,
					subject: props.row.original.subject,
					rawData: props.row.original.rawData,
				}}
			/>
		),
	},
	spacingCol: {
		label: ' ',
		getDisplay: () => '',
		options: {
			viewColumns: false,
			draggable: false,
			excludeFromSave: true,
			groupable: false,
		},
	},
	consultingPhysicianText: {
		label: 'Consulting Physician',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		variableTypeToParseWhenSorting: 'text',
		filterType: 'text-search',
		searchParameter: 'consultingPhysician',
		type: 'string',
		getDisplay: resource =>
			resource.consultingPhysician?.map(physician => getUserFullName(physician, true)).join(' | '),
		getGroupDisplay: row => getGroupContent(row, 'consultingPhysicianText'),
	},
	numDocuments: {
		label: '# of Documents',
		options: { filter: true, sort: true, sortCompare: sortCompareNumber, groupable: true, isGroupExactMatch: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		type: 'number',
		searchParameter: 'numDocuments',
		getDisplay: resource => {
			const displayValue = getExtensionValueString(resource, fhirExtensionUrls.customFields.numDocuments);
			if (displayValue === '0') {
				return;
			}
			return displayValue;
		},
		getGroupDisplay: row => getGroupContent(row, 'numDocuments'),
		getOrignialValue: row => getExtensionValueString(resource, fhirExtensionUrls.customFields.numDocuments),
	},
	visitClass: {
		label: 'Visit Class',
		options: { filter: true, sort: true, groupable: true, isGroupExactMatch: true },
		filterType: 'multi-select',
		searchParameter: 'visitClass',
		searchValueSet: 'encounterclass',
		camelCaseOptions: true,
		type: 'string',
		getDisplay: resource => getPatientClassDisplay(resource.visitClass),
		getGroupDisplay: row => getGroupContent(row, 'visitClass'),
		getOrignialValue: row => row.visitClass,
	},
};

export const ImagingStudyWorklistMappingSearchByUtcDate = {
	id: {
		label: '',
		options: {
			filter: false,
			sort: false,
			display: 'excluded',
			viewColumns: false,
			excludeFromSave: true,
		},
		queryParameter: 'id',
		valueSet: {},
		filterType: 'none',
		getDisplay: resource => resource.id,
	},
	accessionNumber: {
		label: 'Accession #',
		options: { filter: true, sort: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		searchParameter: 'accessionNumber',
		getDisplay: resource =>
			resource?.identifier?.find(elem => elem.type?.coding?.some(item => item.code === 'ACSN'))?.value,
		accessorFn: row => row?.identifier?.find(elem => elem.type?.coding?.some(item => item.code === 'ACSN'))?.value,
	},
	primaryPriorAuthorizationNumber: {
		label: 'Primary Prior Authorization #',
		options: { filter: true, sort: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		searchParameter: 'primarypriorauthorizationnumber',
		accessorFn: row => row.primaryPriorAuthorizationNumber,
	},
	assigningAuthority: {
		label: 'Assigning Authority',
		options: { filter: false, sort: true },
		variableTypeToParseWhenSorting: 'text',
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource =>
			resource.extension?.find(
				ext =>
					ext.url == fhirExtensionUrls.organization.issuer ||
					ext.url == fhirExtensionUrls.organization.defaultIssuer
			)?.valueReference.display,
		accessorFn: row =>
			row.extension?.find(
				ext =>
					ext.url == fhirExtensionUrls.organization.issuer ||
					ext.url == fhirExtensionUrls.organization.defaultIssuer
			)?.valueReference.display,
	},
	accountNumber: {
		label: 'Account #',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'accountNumber',
		getDisplay: resource => resource.accountNumber,
	},
	accountStatus: {
		label: 'Account Status',
		options: { filter: false, sort: false },
		filterType: 'multi-select',
		searchParameter: 'accountStatus',
		searchValueSet: 'accountStatus',
		displayValue: 'code',
		capitalizeOptions: true,
		getDisplay: resource => resource.accountStatus,
	},
	gender: {
		label: 'Gender',
		options: { filter: true, sort: true },
		filterType: 'multi-select',
		searchParameter: 'gender',
		searchValueSet: 'sex',
		getDisplay: resource => resource.gender?.toUpperCase(),
	},
	availability: {
		label: 'Availability',
		options: { filter: false, sort: false },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.availability,
	},
	authorizationPeriod: {
		label: 'Authorization Period',
		options: { filter: false, sort: false },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.authorizationPeriod,
	},
	appointmentCancellationReason: {
		label: 'Appointment Cancellation Reason',
		options: { filter: false, sort: true },
		filterType: 'none',
		searchParameter: 'cancellationReason',
		getDisplay: resource => resource.cancellationReason,
	},
	cdsId: {
		label: 'CDS ID',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'cdsId',
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.procedureRequest.cdsId),
		accessorFn: row => getExtensionValueString(row, fhirExtensionUrls.procedureRequest.cdsId),
	},
	sourceDeviceAETitle: {
		label: 'Source Device AE Title',
		options: { filter: false, sort: true },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.sourceDeviceAETitle,
	},
	smokingStatus: {
		label: 'Smoking Status',
		options: { filter: false, sort: false },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => convertArrayToString(resource.smokingStatus, 'display'),
		accessorFn: row => convertArrayToString(row.smokingStatus, 'display'),
	},
	statusIndicator: {
		label: 'Indicators',
		options: {
			filter: true,
			sort: false,
			useCustomExportFn: true,
		},
		filterType: 'checkbox-multi-select',
		searchParameter: 'statusIndicator',
		customFilterOptions: statusIndicatorFilterOptions,
		accessorFn: (row, index, column) => {
			let statusIndicators = GetStatusIndicatorDisplay(column, row);
			return statusIndicators.indicators.map(indicator => indicator.name)?.join('|');
		},
		cell: props => <StatusIndicatorCell column={props.column} row={props.row.original} />,
	},
	languages: {
		label: 'Languages',
		options: { filter: true, sort: false, groupable: true, isMultiSelect: true },
		filterType: 'multi-select',
		searchParameter: 'language',
		searchValueSet: 'language',
		getDisplay: resource => convertArrayToString(resource.languages),
		accessorFn: row => convertArrayToString(row.languages),
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'language' },
			},
		},
	},
	modality: {
		label: 'Modality',
		options: { filter: true, sort: true, groupable: true, isMultiSelect: true },
		variableTypeToParseWhenSorting: 'text',
		filterType: 'multi-select',
		searchParameter: 'modalitycode',
		searchValueSet: 'modality',
		getDisplay: resource => convertArrayToString(resource.modality, 'code'),
		accessorFn: row => convertArrayToString(row.modality, 'code'),
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.valueSet,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.display} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.display}` }),
			compareOptionFunc: (option, value) => {
				return option.display === value?.tagDisplay || option.id === value?.id;
			},
			searchParameters: {
				sort: 'display',
				search: 'display',
				extraParam: { 'name:exact': 'modality' },
			},
		},
	},
	race: {
		label: 'Race',
		options: { filter: true, sort: false },
		filterType: 'multi-select',
		searchParameter: 'race',
		searchValueSet: 'race',
		getDisplay: resource => convertArrayToString(resource.race),
		accessorFn: row => convertArrayToString(row.race),
	},
	ethnicity: {
		label: 'Ethnicity',
		options: { filter: true, sort: false },
		filterType: 'multi-select',
		searchParameter: 'ethnicity',
		searchValueSet: 'ethnicity',
		getDisplay: resource => {
			if (resource.ethnicity instanceof Array) {
				const filtered = resource.ethnicity.filter(elem => elem.code !== 'UNK');
				return convertArrayToString(filtered);
			}
			return resource.ethnicity?.display || resource.ethnicity;
		},
		accessorFn: row => {
			if (row.ethnicity instanceof Array) {
				const filtered = row.ethnicity.filter(elem => elem.code !== 'UNK');
				return convertArrayToString(filtered);
			}
			return row.ethnicity.display || row.ethnicity;
		},
	},
	patientEmail: {
		label: 'Patient Email',
		options: { filter: true, sort: false },
		filterType: 'text-search',
		searchParameter: 'patientEmail',
		getDisplay: resource => resource.patientEmail,
	},
	cellPhone: {
		label: 'Patient Cell Phone',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'cellPhone',
		getDisplay: resource => resource.cellPhone,
	},
	customField1: {
		label: 'Custom Field 1',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'customField1',
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.common.customField1),
	},
	customField2: {
		label: 'Custom Field 2',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'customField2',
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.common.customField2),
	},
	dateTimeAddendum: {
		label: 'Date/Time Addendum',
		options: { filter: true, sort: true },
		filterType: 'date-range',
		searchParameter: 'datetimeaddendumutc',
		convertToUTC: true,
		getDisplay: resource => worklistOldDateTimeDisplay(resource.dateTimeAddendumUTC),
		accessorFn: row => worklistOldDateTimeDisplay(row.dateTimeAddendumUTC),
		toolTip: true,
	},
	lastUpdated: {
		label: 'Date/Time Last Updated',
		options: { filter: true, sort: true },
		filterType: 'date-range',
		searchParameter: 'lastupdated',
		convertToUTC: true,
		getDisplay: resource => worklistOldDateTimeDisplay(resource.meta?.lastUpdated),
		accessorFn: row => worklistOldDateTimeDisplay(row.meta?.lastUpdated),
		toolTip: true,
	},
	dateTimeOrdered: {
		// this is the date/time that the study transitions to the ORDERED study status
		label: 'Date/Time Ordered',
		options: { filter: true, sort: true },
		variableTypeToParseWhenSorting: 'date',
		filterType: 'date-range',
		searchParameter: 'datetimeorderedutc',
		convertToUTC: true,
		getDisplay: resource => worklistOldDateTimeDisplay(resource.dateTimeOrderedUTC),
		toolTip: true,
	},
	dateTimeRead: {
		label: 'Date/Time Read',
		options: { filter: true, sort: true },
		filterType: 'date-range',
		searchParameter: 'datetimereadutc',
		convertToUTC: true,
		getDisplay: resource => worklistOldDateTimeDisplay(resource.dateTimeReadUTC),
		toolTip: true,
	},
	dateTimeReceived: {
		label: 'Date/Time Received',
		options: { filter: true, sort: true },
		variableTypeToParseWhenSorting: 'date',
		filterType: 'date-range',
		searchParameter: 'datetimereceivedutc',
		convertToUTC: true,
		getDisplay: resource => worklistOldDateTimeDisplay(resource.dateTimeReceivedUTC),
		toolTip: true,
	},
	dateTimeSigned: {
		label: 'Date/Time Signed',
		options: { filter: true, sort: true },
		filterType: 'date-range',
		searchParameter: 'datetimesignedutc',
		convertToUTC: true,
		getDisplay: resource => worklistOldDateTimeDisplay(resource.dateTimeSignedUTC),
		toolTip: true,
	},
	dateTimeTranscribed: {
		label: 'Date/Time Transcribed',
		options: { filter: true, sort: true },
		filterType: 'date-range',
		searchParameter: 'datetimetranscribedutc',
		convertToUTC: true,
		getDisplay: resource => worklistOldDateTimeDisplay(resource.dateTimeTranscribedUTC),
		toolTip: true,
	},
	dateTimeVerified: {
		label: 'Date/Time Verified',
		options: { filter: true, sort: true },
		filterType: 'date-range',
		searchParameter: 'datetimeverifiedutc',
		convertToUTC: true,
		getDisplay: resource => worklistOldDateTimeDisplay(resource.dateTimeVerifiedUTC),
		toolTip: true,
	},
	dateTimeScheduled: {
		label: 'Status To Scheduled',
		options: { filter: true, sort: true },
		filterType: 'date-range',
		searchParameter: 'datetimescheduled',
		getDisplay: resource => worklistOldDateTimeDisplay(resource.dateTimeScheduled, false),
		toolTip: true,
	},
	appointmentDateTime: {
		label: 'Scheduled Date/Time',
		options: { filter: true, sort: true },
		variableTypeToParseWhenSorting: 'date',
		filterType: 'date-range',
		searchParameter: 'appointmentDateTime',
		getDisplay: resource => worklistOldDateTimeDisplay(resource.appointmentDateTime),
		toolTip: true,
	},
	department: {
		label: 'Department',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'department',
		getDisplay: resource => resource.department,
	},
	eligibility: {
		label: 'Eligibility',
		options: { filter: false, sort: true },
		filterType: 'multi-select',
		searchParameter: 'eligibility',
		searchValueSet: 'ordereligibility',
		getDisplay: resource =>
			getExtensionValueCoding(resource, fhirExtensionUrls.procedureRequest.eligibility).display,
	},
	communicationStatusName: {
		label: 'Communication Status Name',
		options: { filter: false, sort: true },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.communicationStatusName,
	},
	fillerOrderNumber: {
		label: 'Filler Order #',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'fillerOrderNumber',
		getDisplay: resource => resource.fillerOrderNumber,
	},
	homePhone: {
		label: 'Home Phone',
		options: { filter: false, sort: true },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.homePhone,
	},
	patientAge: {
		label: 'Age at Study',
		options: { filter: false, sort: true, sortCompare: sortCompareNumber },
		filterType: 'none',
		type: 'number',
		searchParameter: '',
		getDisplay: resource => calculatePatientAgeAtStudy(resource),
	},
	numImages: {
		label: '# of Images',
		options: { filter: true, sort: true, sortCompare: sortCompareNumber },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		type: 'number',
		searchParameter: 'numImages',
		getDisplay: resource => resource.numImages,
	},
	insuranceCopay: {
		label: 'Insurance Copay',
		options: { filter: true, sort: false },
		filterType: 'text-search',
		searchParameter: 'insuranceCopay',
		getDisplay: resource => resource.insuranceCopay,
	},
	insuranceExpiry: {
		label: 'Insurance Expiry',
		options: { filter: false, sort: false },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.insuranceExpiry,
	},
	insurancePayer: {
		label: 'Primary Insurance',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'payer',
		getDisplay: resource => resource.insurancePayer?.display,
	},
	insuranceStatus: {
		label: 'Insurance Status',
		options: { filter: false, sort: false },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.insuranceStatus,
	},
	managingOrganization: {
		label: 'Managing Organization',
		options: { filter: true, sort: true },
		variableTypeToParseWhenSorting: 'text',
		filterType: 'suggest',
		searchParameter: 'internalManagingOrganizationID',
		suggestScope: {
			resource: 'organization',
			label: 'name',
			extraParam: {
				organizationtype: 'PROV',
			},
		},
		getDisplay: resource => resource.managingOrganization,
	},
	patientLocation: {
		label: 'Patient Location',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'location',
		getDisplay: resource => resource.patientLocation,
	},
	numFrames: {
		label: '# of Frames',
		options: { filter: true, sort: true, sortCompare: sortCompareNumber },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		type: 'number',
		searchParameter: 'numFrames',
		getDisplay: resource => resource.numFrames,
	},
	numberOfInstances: {
		label: '# of Instances',
		options: {
			filter: false,
			sort: true,
			sortCompare: sortCompareNumber,
		},
		variableTypeToParseWhenSorting: 'number',
		filterType: 'none',
		type: 'number',
		searchParameter: 'numinstances',
		getDisplay: resource => resource.numberOfInstances,
	},
	numReports: {
		label: '# of Reports',
		options: { filter: false, sort: true, sortCompare: sortCompareNumber },
		filterType: 'none',
		type: 'number',
		searchParameter: 'numReports',
		getDisplay: resource => resource.numReports,
	},
	orderCustomField1: {
		label: 'Order Custom Field 1',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'orderCustomField1',
		getDisplay: resource =>
			getExtensionValueString(resource, fhirExtensionUrls.imagingStudyWorklist.orderCustomField1),
	},
	orderCustomField2: {
		label: 'Order Custom Field 2',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'orderCustomField2',
		getDisplay: resource =>
			getExtensionValueString(resource, fhirExtensionUrls.imagingStudyWorklist.orderCustomField2),
	},
	orderCustomMemo: {
		label: 'Order Custom Memo',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'orderCustomMemo',
		getDisplay: resource =>
			getExtensionValueString(resource, fhirExtensionUrls.imagingStudyWorklist.orderCustomMemo1),
	},
	orderDateTime: {
		label: 'Order Placement Date/Time',
		options: { filter: true, sort: true },
		filterType: 'date-range',
		searchParameter: 'orderdatetimeutc',
		convertToUTC: true,
		getDisplay: resource => worklistOldDateTimeDisplay(resource.orderDateTimeUTC),
		toolTip: true,
	},
	orderStatus: {
		label: 'Order Status',
		options: { filter: true, sort: true },
		filterType: 'multi-select',
		searchParameter: 'orderstatus',
		searchValueSet: 'requeststatus',
		displayValue: 'code',
		getDisplay: resource => resource.orderStatus,
	},
	imagingOrganization: {
		label: 'Imaging Organization',
		options: { filter: false, sort: true },
		filterType: 'suggest',
		searchParameter: 'imagingOrganizationID',
		suggestScope: {
			resource: 'organization',
			label: 'name',
			extraParam: {
				organizationtype: 'PROV',
				isimaging: true,
			},
		},
		getDisplay: resource => resource.imagingOrganization,
	},
	financialClass: {
		label: 'Financial Class',
		options: { filter: false, sort: true },
		filterType: 'multi-select',
		searchParameter: 'financialClass',
		searchValueSet: 'financialClass',
		getDisplay: resource => resource.financialClass?.display,
	},
	healthcareService: {
		label: 'Healthcare Service',
		options: { filter: false, sort: true },
		filterType: 'multi-select',
		searchParameter: 'healthcareService',
		searchValueSet: 'healthcareService',
		accessorFn: row => row.healthcareServiceName || row.HealthcareServiceName,
	},
	orderOfAppropriateness: {
		label: 'Order Of Appropriateness',
		options: { filter: false, sort: true },
		filterType: 'multi-select',
		searchParameter: 'orderappropriateness',
		searchValueSet: 'appropriateness',
		getDisplay: resource =>
			getExtensionValueCoding(resource, fhirExtensionUrls.procedureRequest.cdsAppropriate).display,
	},
	pharmaceutical: {
		label: 'Pharmaceutical',
		options: { filter: false, sort: false },
		filterType: 'multi-select',
		searchParameter: 'pharmaceutical',
		searchValueSet: 'pharmaceutical',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.pharmaceutical),
	},
	patientBalance: {
		label: 'Patient Balance',
		options: { filter: false, sort: true },
		filterType: 'none',
		searchParameter: 'patientBalance',
		getDisplay: resource => resource.patientBalance,
	},
	patientID: {
		label: 'Patient ID',
		options: { filter: true, sort: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		searchParameter: 'patientID',
		getDisplay: resource => resource?.patientID,
	},
	performingPhysician: {
		label: 'Performing Physician',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'performingPhysician',
		getDisplay: resource => getUserFullName(resource.performingPhysician, true),
		cell: props =>
			!props?.renderText ? (
				<InlineEditCell
					size="small"
					userName={props.getValue()}
					internalStudyId={props.row.original.id}
					searchParams={{
						ispractitionerroleactive: true,
						managingorganization: props.row.original?.internalManagingOrganizationID,
						isperformingphysician: true,
					}}
					onUpdate={performingPhysicianName => {
						props.table.options.meta?.updateData(
							props.row.index,
							'performingPhysician',
							performingPhysicianName
						);
					}}
					cellLabel="performingPhysician"
					label="performing physician"
					checkStudyReadOnly={true}
					organizationId={getOrganizationId(props.row.original)}
					studyStatus={getStudyStatus(props.row.original)}
				/>
			) : (
				<span>{getUserFullName(props.row.original?.performingPhysician)}</span>
			),
	},
	performingPhysicianNPI: {
		label: 'Performing Physician NPI',
		options: { filter: true, sort: false },
		filterType: 'text-search',
		searchParameter: 'performingPhysicianNPI',
		getDisplay: resource => resource.performingPhysicianNPI,
	},
	performingTechnologist: {
		label: 'Performing Technologist',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'performingTechnologist',
		getDisplay: resource => resource.performingTechnologist,
		cell: props =>
			!props?.renderText ? (
				<InlineEditCell
					size="small"
					userName={props.getValue()}
					internalStudyId={props.row.original.id}
					searchParams={{
						ispractitionerroleactive: true,
						managingorganization: props.row.original?.internalManagingOrganizationID,
						isperformingtechnologist: true,
					}}
					onUpdate={performingTechnologistName => {
						props.table.options.meta?.updateData(
							props.row.index,
							'performingTechnologist',
							performingTechnologistName
						);
					}}
					cellLabel="performingTechnologist"
					label="performing technologist"
					checkStudyReadOnly={true}
					organizationId={getOrganizationId(props.row.original)}
					studyStatus={getStudyStatus(props.row.original)}
				/>
			) : (
				<span>{props.row.original?.performingTechnologist}</span>
			),
	},
	placerOrderNumber: {
		label: 'Placer Order #',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'placerOrderNumber',
		getDisplay: resource => resource.placerOrderNumber,
	},
	procedureCode: {
		label: 'Procedure Code',
		options: { filter: false, sort: false },
		filterType: 'multi-select',
		mavenSAYTNewComponent: true,
		searchComponentConfig: {
			scope: searchScopes?.procedureCode,
			renderOptionFunc: (props, selected, data) => (
				<li {...props}>
					<Checkbox
						checked={selected}
						checkedIcon={<CheckBoxIcon fontSize="small" />}
						icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					/>
					<OverflowTooltip text={data.code} />
				</li>
			),
			renderTagFunc: data => ({ tagDisplay: `${data.code}` }),
			compareOptionFunc: (option, value) => option.code === value?.tagDisplay || option.id === value?.id,
			searchParameters: {
				sort: 'code',
				search: 'code',
			},
		},
		searchParameter: 'procedureCode',
		displayValue: 'code',
		getDisplay: resource => {
			if (resource.procedureCode) {
				const procedureCodeDisplay = [];
				const procedurecodeLength =
					resource.procedureCode && resource.procedureCode[0] && resource.procedureCode[0].coding
						? resource.procedureCode[0].coding.length
						: 0;
				let item;

				for (let i = 0; i < procedurecodeLength; i++) {
					item = resource.procedureCode[0]?.coding[i];
					if (item && item.code) {
						procedureCodeDisplay.push(item.code);
					} else if (item && item.display) {
						procedureCodeDisplay.push(item.display);
					}
				}
				return convertArrayToString(procedureCodeDisplay);
			}

			return resource.procedureCode;
		},
	},
	readingFacility: {
		label: 'Reading Organization',
		options: { filter: false, sort: true },
		filterType: 'suggest',
		searchParameter: 'readingFacility',
		suggestScope: {
			resource: 'organization',
			label: 'name',
			extraParam: {
				organizationtype: 'PROV',
				isreading: true,
			},
		},
		getDisplay: resource => resource.readingFacility,
	},
	readingPhysician: {
		label: 'Reading Physician',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'readingPhysician',
		getDisplay: resource => resource.readingPhysician,
		cell: props =>
			!props?.renderText ? (
				<InlineEditCell
					size="small"
					userName={props.getValue()}
					internalStudyId={props.row.original.id}
					searchParams={{
						ispractitionerroleactive: true,
						managingorganization: props.row.original?.internalManagingOrganizationID,
						isreadingphysician: true,
					}}
					onUpdate={readingPhysicianName => {
						props.table.options.meta?.updateData(props.row.index, 'readingPhysician', readingPhysicianName);
					}}
					cellLabel="readingPhysician"
					label="reading physician"
					checkStudyReadOnly={true}
					organizationId={getOrganizationId(props.row.original)}
					studyStatus={getStudyStatus(props.row.original)}
				/>
			) : (
				<span>{props.row.original?.readingPhysician}</span>
			),
	},
	readingPhysicianNPI: {
		label: 'Reading Physician NPI',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'readingPhysicianNPI',
		getDisplay: resource => resource.readingPhysicianNPI,
	},
	patientAddress: {
		label: 'Patient Address',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'address',
		getDisplay: resource => resource.patientAddress,
	},
	referringFacility: {
		label: 'Referring Organization',
		options: { filter: false, sort: true },
		filterType: 'suggest',
		searchParameter: 'referringFacilityID',
		suggestScope: {
			resource: 'organization',
			label: 'name',
			extraParam: {
				organizationtype: 'PROV',
				isreferring: true,
			},
		},
		getDisplay: resource => resource.referringFacility,
	},
	referringPhysician: {
		label: 'Referring Physician',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'referringPhysician',
		variableTypeToParseWhenSorting: 'text',
		getDisplay: resource => ({
			accessionNumber: resource.identifier?.find(elem => elem.type?.coding?.some(item => item.code === 'ACSN'))
				?.value,
			referringPhysician: getUserFullName(resource.referringPhysician),
		}),
		cell: props =>
			!props?.renderText ? (
				<InlineEditCell
					size="small"
					userName={props.getValue()}
					searchParams={{
						ispractitionerroleactive: true,
						managingorganization: props.row.original?.internalManagingOrganizationID,
						isreferringphysician: true,
						_summary: true,
						_elements: 'name',
						active: true,
					}}
					onUpdate={referringPhysicianName => {
						props.table.options.meta?.updateData(
							props.row.index,
							'referringPhysician',
							referringPhysicianName
						);
					}}
					cellLabel="referringPhysician"
					internalOrderId={props.row.original?.basedOn?.[0]?.id}
					referringPhysicianId={props.row.original?.referringPhysicianId}
					label="referring physician"
				/>
			) : (
				<span>{getUserFullName(props.row.original?.referringPhysician)}</span>
			),
		accessorFn: row => getUserFullName(row.referringPhysician),
	},
	referringPhysicianNPI: {
		label: 'Referring Physician NPI',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'referringPhysicianNPI',
		getDisplay: resource => resource.referringPhysicianNPI,
	},
	requestedAppointmentDateTime: {
		label: 'Requested Appointment Date/Time',
		options: { filter: true, sort: true },
		filterType: 'date-range',
		searchParameter: 'requestedappointmentdatetimeutc',
		convertToUTC: true,
		getDisplay: resource => worklistOldDateTimeDisplay(resource.requestedAppointmentDateTimeUTC),
		toolTip: true,
	},
	birthDate: {
		label: 'Date of Birth',
		options: { filter: false, sort: true },
		filterType: 'date-time',
		searchParameter: 'birthdate:exact',
		getDisplay: resource => {
			const value = resource.birthDate;
			if (value != null) {
				const dateTime = new Date(value);
				if (isValidDateMapping(dateTime)) {
					return moment(resource.birthDate).format('MM/DD/YYYY');
				}
			}
		},
		toolTip: true,
	},
	birthSex: {
		label: 'Birth Sex',
		options: { filter: true, sort: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'multi-select',
		searchParameter: 'sex',
		searchValueSet: 'sex',
		getDisplay: resource =>
			resource.extension?.find(elem => elem.url === fhirExtensionUrls.common.birthSex)?.valueCode,
	},
	ssn: {
		label: 'SSN',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'ssn',
		getDisplay: resource => resource.ssn,
	},
	state: {
		label: 'Patient State/Province',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'state',
		getDisplay: resource => resource.state,
	},
	studyCustomMemo: {
		label: 'Study Custom Memo',
		options: { filter: false, sort: true },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.studyCustomMemo,
	},
	studyComment: {
		label: 'Study Comment',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'studyComment',
		getDisplay: resource =>
			resource.extension?.find(elem => elem.url === fhirExtensionUrls.imagingStudy.studyComments)?.valueString,
	},
	studyID: {
		label: 'Study ID',
		options: { filter: true, sort: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		searchParameter: 'studyID',
		getDisplay: resource => resource.studyID,
	},
	studyUID: {
		label: 'Study UID',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'studyUID',
		getDisplay: resource =>
			resource.identifier
				? resource.identifier?.find(elem => elem.system === 'urn:dicom:uid')?.value.replace(/^(urn:oid:)/, '')
				: '',
	},
	seriesUID: {
		label: 'Series UID',
		options: {
			filter: false,
			sort: false,
			display: 'excluded',
			viewColumns: false,
		},
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource =>
			getExtensionValueString(resource, fhirExtensionUrls.imagingStudyWorklist.seriesInstanceUID),
	},
	sopInstanceUID: {
		label: 'SOP Instance UID',
		options: {
			filter: false,
			sort: false,
			display: 'excluded',
			viewColumns: false,
		},
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource =>
			getExtensionValueString(resource, fhirExtensionUrls.imagingStudyWorklist.sopInstanceUID),
	},
	timeAtStatus: {
		label: 'Time At Status',
		options: { filter: false, sort: true },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.timeAtStatus,
	},
	timeZone: {
		label: 'Time Zone',
		options: { filter: false, sort: true },
		filterType: 'multi-select',
		searchParameter: 'timeZone',
		searchValueSet: 'timeZone',
		getDisplay: resource => resource.timeZone,
	},
	transcriptionist: {
		label: 'Transcriptionist',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'transcriptionist',
		getDisplay: resource => resource.transcriptionist,
		cell: props =>
			!props?.renderText ? (
				<InlineEditCell
					size="small"
					userName={props.getValue()}
					internalStudyId={props.row.original.id}
					searchParams={{
						ispractitionerroleactive: true,
						managingorganization: props.row.original?.internalManagingOrganizationID,
						istranscriptionist: true,
					}}
					onUpdate={transcriptionistName => {
						props.table.options.meta?.updateData(props.row.index, 'transcriptionist', transcriptionistName);
					}}
					cellLabel="transcriptionist"
					label="transcriptionist"
					checkStudyReadOnly={true}
					organizationId={getOrganizationId(props.row.original)}
					studyStatus={getStudyStatus(props.row.original)}
				/>
			) : (
				<span>{props.row.original?.transcriptionist}</span>
			),
	},
	transcriptionistFacility: {
		label: 'Transcriptionist Organization',
		options: { filter: false, sort: true },
		filterType: 'suggest',
		searchParameter: 'transcriptionistFacility',
		suggestScope: {
			resource: 'organization',
			label: 'name',
			extraParam: {
				organizationtype: 'PROV',
				istranscription: true,
			},
		},
		getDisplay: resource => resource.transcriptionistFacility,
	},
	specialArrangement: {
		label: 'Special Arrangement',
		options: { filter: false, sort: false },
		filterType: 'multi-select',
		searchParameter: 'specialArrangement',
		searchValueSet: 'specialArrangement',
		getDisplay: resource => resource.specialArrangement,
	},
	visitNumber: {
		label: 'Visit #',
		options: { filter: false, sort: true },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.visitNumber,
	},
	orderNotes: {
		label: 'Order Notes',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'orderNotes',
		getDisplay: resource => resource.orderNotes,
	},
	clinicalNotes: {
		label: 'Clinical Notes',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'clinical',
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.imagingStudy.notes.clinical),
	},
	history: {
		label: 'History',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'history',
		getDisplay: resource => resource.history,
	},
	laterality: {
		label: 'Laterality',
		options: { filter: false, sort: false },
		filterType: 'multi-select',
		searchParameter: 'laterality',
		searchValueSet: 'laterality',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.laterality),
	},
	bodyPart: {
		label: 'Body Part',
		options: { filter: false, sort: true },
		filterType: 'multi-select',
		searchParameter: 'bodyPart',
		searchValueSet: 'bodyPart',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.bodyPart),
	},
	modalityModifierArray: {
		label: 'Modality Modifier Array',
		options: { filter: false, sort: false },
		filterType: 'none',
		searchParameter: 'notes',
		getDisplay: resource => resource.modalityModifierArray,
	},
	anatomicFocus: {
		label: 'Anatomic Focus',
		options: { filter: false, sort: false },
		filterType: 'multi-select',
		searchParameter: 'anatomicFocus',
		searchValueSet: 'anatomicFocus',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.anatomicFocus),
	},
	priority: {
		label: 'Priority',
		options: { filter: false, sort: true },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'multi-select',
		searchParameter: 'priority',
		searchValueSet: 'priority',
		getDisplay: resource => {
			const routineValue =
				getExtensionValueReference(resource, fhirExtensionUrls.studyPriority.customPriority)?.display ||
				getExtensionValueCoding(resource, fhirExtensionUrls.common.priority)?.display;
			return routineValue?.toUpperCase();
		},
		cell: props => (
			<InlineEditCell
				size="small"
				cellValue={props}
				searchValueSet="priority"
				onUpdate={(priority, proactEnableCustomStudyPriority, updatedPriority) => {
					const extensionUrl = fhirExtensionUrls.common.priority;
					if (proactEnableCustomStudyPriority) {
						const customPriorityIdx = props.row.original.extension.findIndex(
							ext => ext.url == fhirExtensionUrls.studyPriority.customPriority
						);
						if (customPriorityIdx === -1) {
							const customExtension = {
								url: fhirExtensionUrls.studyPriority.customPriority,
								valueReference: updatedPriority,
							};
							props.row.original.extension.push(customExtension);
						} else {
							props.table.options.meta?.updateData(
								props.row.index,
								`extension[${customPriorityIdx}]valueReference`,
								updatedPriority
							);
						}
					} else {
						props.table.options.meta?.updateData(props.row.index, 'priority', priority, extensionUrl);
					}
				}}
				cellLabel="priority"
				internalOrderId={props.row.original?.basedOn?.[0]?.id}
				imagingOrganizationId={props?.row?.original?.internalManagingOrganizationID}
			/>
		),
	},
	reasonForExam: {
		label: 'Reason For Exam',
		options: { filter: false, sort: false },
		filterType: 'multi-select',
		searchParameter: 'reasonForExam',
		searchValueSet: 'reasonForExam',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.reasonForExam),
	},
	view: {
		label: 'View',
		options: { filter: false, sort: true },
		filterType: 'multi-select',
		searchParameter: 'view',
		searchValueSet: 'view',
		getDisplay: resource => getExtensionValueCodingArrayConcat(resource, fhirExtensionUrls.common.view),
	},
	techniqueObject: {
		label: 'Technique Object',
		options: { filter: false, sort: true },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => resource.techniqueObject,
	},
	studyDateTime: {
		label: 'Study Date/Time',
		options: { filter: true, sort: true },
		variableTypeToParseWhenSorting: 'date',
		filterType: 'date-range',
		searchParameter: 'studydatetime',
		getDisplay: resource => worklistOldDateTimeDisplay(resource.started),
		accessorFn: row => worklistOldDateTimeDisplay(row.started),
		toolTip: true,
	},
	started: {
		label: 'Started',
		options: { filter: false, sort: true },
		filterType: 'none',
		searchParameter: '',
		getDisplay: resource => {
			const value = resource.started;
			if (value != null) {
				const dateTime = new Date(value);
				if (!isNaN(dateTime.getTime()) && dateTime.getYear() >= 0) {
					return resource.started;
				}
			}
		},
	},
	patientName: {
		label: 'Patient Name',
		options: {
			filter: true,
			sort: true,
			setCellProps: () => ({ style: { width: '0%' } }),
		},
		variableTypeToParseWhenSorting: 'text',
		filterType: 'text-search',
		queryType: 'multi-match',
		searchParameter: 'patientName',
		getDisplay: resource => `${getUserFullName(resource.subject?.display)}`,
		cell: props => <PatientProfileCell data={props.row.original} />,
	},
	patientgiven: {
		label: 'Patient First Name',
		options: {
			filter: true,
			sort: true,
			setCellProps: () => ({ style: { width: '0%' } }),
		},
		variableTypeToParseWhenSorting: 'text',
		filterType: 'text-search',
		searchParameter: 'patientgiven',
		getDisplay: resource => `${getWorklistParsedName(resource?.subject?.display)?.firstName}`,
		cell: props => getWorklistParsedName(props?.row?.original?.subject?.display)?.firstName,
	},
	patientfamily: {
		label: 'Patient Last Name',
		options: {
			filter: true,
			sort: true,
			setCellProps: () => ({ style: { width: '0%' } }),
		},
		variableTypeToParseWhenSorting: 'text',
		filterType: 'text-search',
		searchParameter: 'patientfamily',
		getDisplay: resource => `${getWorklistParsedName(resource?.subject?.display)?.lastName}`,
		cell: props => getWorklistParsedName(props?.row?.original?.subject?.display)?.lastName,
	},
	studyStatus: {
		label: 'Study Status',
		options: {
			filter: true,
			sort: true,
			customBodyRender: (value, tableMeta, updateValue) =>
				value?.props.text ? (
					<StudyStatusCellProvider
						studyId={value.props.text.studyId}
						status={value.props.text.status}
						onUpdate={(studyId, studyStatus) => {
							updateValue(
								<OverflowTooltip
									enableRecalc={false}
									text={{
										studyId,
										status: studyStatus,
									}}
								/>
							);
						}}
					/>
				) : null,
			setCellProps: () => ({ style: { width: '0%' } }),
		},
		filterType: 'multi-select',
		searchParameter: 'status',
		searchValueSet: 'status',
		getDisplay: resource => ({
			studyId: resource.id,
			status: getExtensionValueCoding(resource, fhirExtensionUrls.common.studyStatus).display,
		}),
		accessorFn: row => getExtensionValueCoding(row, fhirExtensionUrls.common.studyStatus)?.display,
		cell: props => (
			<StudyStatusCellProvider
				studyId={props.row.original.id}
				status={props.getValue()}
				orgId={props.row.original?.imagingOrganizationId || props.row.original.internalManagingOrganizationID}
				onUpdate={value => {
					const studyStatusIdx = props.row.original.extension?.findIndex(
						ext => ext.url == fhirExtensionUrls.common.studyStatus
					);
					props.table.options.meta?.updateData(
						props.row.index,
						`extension[${studyStatusIdx}]valueCoding.display`,
						value
					);
				}}
				rejectionReasonArr={getExtensionValueCodingArray(
					props.row.original,
					fhirExtensionUrls.imagingStudy.rejectionReason
				)}
				managingOrganization={props.row.original?.managingOrganization}
				managingOrganizationId={props.row.original?.internalManagingOrganizationID}
			/>
		),
	},
	studyDescription: {
		label: 'Study Description',
		options: { filter: true, sort: false },
		filterType: 'text-search',
		searchParameter: 'description',
		getDisplay: resource => resource.description,
	},
	requestedProcedureID: {
		label: 'Requested Procedure ID',
		options: { filter: true, sort: true },
		filterType: 'text-search',
		searchParameter: 'requestedProcedureID',
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.imagingStudy.requestedProcedureID),
	},
	workflowStep: {
		label: 'Workflow Step',
		options: { filter: true, sort: true },
		filterType: 'multi-select',
		searchParameter: 'workflowStep',
		searchValueSet: 'workflowStatus',
		valueSetExtraParam: { operator: '=' },
		getDisplay: resource => getExtensionValueString(resource, fhirExtensionUrls.organization.workflowStep),
	},
	flagCol: {
		label: ' ',
		customHeader: props => <CheckboxSelectAll rows={props.rows} />,
		getDisplay: resource => {
			const dicomUid = resource.identifier
				?.find(id => id.system === 'urn:dicom:uid')
				?.value?.split('urn:oid:')?.[1];

			return JSON.stringify({
				id: resource.id,
				isCritical: !!resource.extension?.find(elem => elem.url === fhirExtensionUrls.imagingStudy.isCritical)
					?.valueBoolean,
				dicomUid,
				internalManagingOrganizationId: resource.internalManagingOrganizationID,
				referringPhysicianId: resource.referringPhysicianId,
				accessionNumber: resource.identifier?.find(elem =>
					elem.type?.coding?.some(item => item.code === 'ACSN')
				)?.value,
			});
		},
		options: {
			customBodyRender: value => <CheckBoxOrCritical text={value.props.text} />,
			setCellProps: () => ({ style: { width: '0%' } }),
			viewColumns: false,
			draggable: false,
			excludeFromSave: true,
		},
		cell: props => (
			<CheckboxCell
				data={{
					id: props.row.original.id,
					isCritical: !!props.row.original.extension?.find(
						elem => elem.url === fhirExtensionUrls.imagingStudy.isCritical
					)?.valueBoolean,
					dicomUid: props.row.original.identifier
						?.find(id => id.system === 'urn:dicom:uid')
						?.value?.split('urn:oid:')?.[1],
					internalManagingOrganizationId: props.row.original.internalManagingOrganizationID,
					referringPhysician: props.row.original.referringPhysician,
					referringPhysicianId: props.row.original.referringPhysicianId,
					accessionNumber: props.row.original.identifier?.find(elem =>
						elem.type?.coding?.some(item => item.code === 'ACSN')
					)?.value,
					basedOn: props.row.original.basedOn,
					patientID: props.row.original.patientID,
					patientEmail: props.row.original.patientEmail,
					subject: props.row.original.subject,
					rawData: props.row.original.rawData,
				}}
			/>
		),
	},
	flagColTF: {
		label: ' ',
		customHeader: props => <CheckboxSelectAll rows={props.rows} />,
		getDisplay: resource => {
			const dicomUid = resource.identifier
				?.find(id => id.system === 'urn:dicom:uid')
				?.value?.split('urn:oid:')?.[1];

			return JSON.stringify({
				id: resource.id,
				isCritical: !!resource.extension?.find(elem => elem.url === fhirExtensionUrls.imagingStudy.isCritical)
					?.valueBoolean,
				dicomUid,
				internalManagingOrganizationId: resource.internalManagingOrganizationID,
				referringPhysicianId: resource.referringPhysicianId,
				accessionNumber: resource.identifier?.find(elem =>
					elem.type?.coding?.some(item => item.code === 'ACSN')
				)?.value,
			});
		},
		options: {
			customBodyRender: value => <CheckBoxOrCritical text={value.props.text} />,
			setCellProps: () => ({ style: { width: '0%' } }),
			viewColumns: false,
			draggable: false,
			excludeFromSave: true,
		},
		cell: props => (
			<CheckboxCell
				data={{
					id: props.row.original.id,
					isCritical: !!props.row.original.extension?.find(
						elem => elem.url === fhirExtensionUrls.imagingStudy.isCritical
					)?.valueBoolean,
					dicomUid: props.row.original.identifier
						?.find(id => id.system === 'urn:dicom:uid')
						?.value?.split('urn:oid:')?.[1],
					internalManagingOrganizationId: props.row.original.internalManagingOrganizationID,
					referringPhysician: props.row.original.referringPhysician,
					referringPhysicianId: props.row.original.referringPhysicianId,
					accessionNumber: props.row.original.identifier?.find(elem =>
						elem.type?.coding?.some(item => item.code === 'ACSN')
					)?.value,
					basedOn: props.row.original.basedOn,
					patientID: props.row.original.patientID,
					patientEmail: props.row.original.patientEmail,
					subject: props.row.original.subject,
					rawData: props.row.original.rawData,
				}}
			/>
		),
	},
	spacingCol: {
		label: ' ',
		getDisplay: () => '',
		options: {
			viewColumns: false,
			draggable: false,
			excludeFromSave: true,
		},
	},
	consultingPhysicianText: {
		label: 'Consulting Physician',
		options: { filter: true, sort: true },
		variableTypeToParseWhenSorting: 'text',
		filterType: 'text-search',
		searchParameter: 'consultingPhysician',
		getDisplay: resource =>
			resource.consultingPhysician?.map(physician => getUserFullName(physician, true)).join(' | '),
	},
	numDocuments: {
		label: '# of Documents',
		options: { filter: true, sort: true, sortCompare: sortCompareNumber },
		variableTypeToParseWhenSorting: 'number',
		filterType: 'text-search',
		type: 'number',
		searchParameter: 'numDocuments',
		getDisplay: resource => {
			const displayValue = getExtensionValueString(resource, fhirExtensionUrls.customFields.numDocuments);
			if (displayValue === '0') {
				return;
			}
			return displayValue;
		},
	},
	visitClass: {
		label: 'Visit Class',
		options: { filter: true, sort: true, groupable: true },
		filterType: 'multi-select',
		searchParameter: 'visitClass',
		searchValueSet: 'encounterclass',
		camelCaseOptions: true,
		type: 'string',
		getDisplay: resource => getPatientClassDisplay(resource.visitClass),
	},
};

export default ImagingStudyWorklistMapping;

function CheckBoxOrCritical({ text }) {
	const { selectedStudies, toggleStudyPresence } = useContext(SelectionContext);

	const json = JSON.parse(text);
	const { isCritical } = json;

	const isSelected = selectedStudies.map(s => s.id).includes(json.id);

	return (
		<Box
			sx={{
				height: '20px',
				width: '20px',

				'&:hover': {
					'.checkbox': { display: 'flex' },
					'.flag': { display: 'none' },
				},
			}}
		>
			<RowCheckbox id={json.id} data={json} isSelected={isSelected} toggleStudyPresence={toggleStudyPresence} />

			<Box className="flag">
				{isCritical && !isSelected ? (
					<FlagOutlinedIcon
						fontSize="small"
						sx={{
							color: '#CF6679',
							border: '#CF6679',
							borderRadius: '6px',
							backgroundColor: 'rgba(207, 102, 121, 0.1)',
							width: '20px',
							height: '20px',
						}}
					/>
				) : null}
			</Box>
		</Box>
	);
}

function RowCheckbox({ id, data, isSelected, toggleStudyPresence }) {
	return (
		<Box
			className="checkbox"
			sx={{
				display: isSelected ? 'flex' : 'none',
			}}
		>
			<Checkbox
				checked={isSelected}
				checkedIcon={
					<Box
						sx={{
							height: '20px',
							width: '20px',
							borderRadius: '6px',
							background: '#42A5F51A',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
						}}
					>
						<CheckIcon
							sx={{
								color: 'rsPrimary.main',
								width: '14px',
								height: '14px',
							}}
						/>
					</Box>
				}
				icon={
					<Box
						sx={{
							height: '18px',
							width: '18px',
							borderRadius: '6px',
							border: '1px solid rgba(255,255,255,.2)',
						}}
					/>
				}
				sx={{
					padding: '0 !important',
				}}
				onClick={e => {
					e.preventDefault();
					e.stopPropagation();
					toggleStudyPresence({
						id,
						data,
					});
				}}
			/>
		</Box>
	);
}
export const getOrganizationId = resource => {
	return resource?.imagingOrganizationId || resource?.internalManagingOrganizationID;
};
export const getStudyStatus = resource => {
	return resource?.extension?.find(ext => ext.url == fhirExtensionUrls.common.studyStatus)?.valueCoding?.display;
};
